import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import doorPngUrl from "./img/porta.png";
import sfondoPngUrl from "./img/sfondo.png";
import soundtrackUrl from "./assets/soundtrack.mp3";

/* ─────────────────────────── CONFIGURATION ────────────────────────────────── */
// Set to true to use PNG images for characters instead of programmatic drawing.
// Expected files in the same folder as this component:
//   Clients : cliente1.png … cliente10.png
//   Bandits : bandito1.png … bandito10.png
const isPNGImagesActive = true;

// Base path for PNG assets (change if images live in a sub-folder, e.g. "/assets/")
const PNG_BASE_PATH = "./img/";

// Caches the door PNG asset so closed doors can render the image when enabled.
let doorPngImage = null;
let doorPngReady = false;
let doorPngLoadStarted = false;

// Loads the door PNG once and triggers the callback after it is ready.
function ensureDoorPngLoaded(resolvePngUrl, onReady){
  if(doorPngReady){ if(onReady) onReady(); return; }
  if(doorPngLoadStarted) return;
  doorPngLoadStarted = true;
  try{
    const img = new Image();
    img.onload = () => { doorPngImage = img; doorPngReady = true; if(onReady) onReady(); };
    img.onerror = (err) => console.warn("[TheBankGame] Could not load door PNG:", err);
    img.src = doorPngUrl || resolvePngUrl("porta.png");
  } catch (err){
    console.warn("[TheBankGame] Door PNG load failed:", err);
  }
}

/* ─────────────────────────── WORLD CONSTANTS ──────────────────────────────── */
const LW = 960, LH = 580;
const DW = 252, DH = 330;
const DX = [-305, 0, 305];
const DOOR_CY = -LH / 2 + 148 + DH / 2;   // centre-Y of doors
const LEVEL_MAX = 99;
const LEVEL_DURATION_SECONDS = 30;
const LEVEL_TRANSITION_MS = 1500;
const LEVEL_POST_TRANSITION_SPAWN_DELAY_MS = 1000;
const BANDIT_SHOT_PRE_DEATH_MS = 220;

/* ─────────────────────────── HELPERS ──────────────────────────────────────── */
const hc = h => new THREE.Color(((h>>16)&255)/255,((h>>8)&255)/255,(h&255)/255);
function mkRect(sc,x,y,w,h,col,z=0){
  const m=new THREE.Mesh(new THREE.PlaneGeometry(w,h),
    new THREE.MeshBasicMaterial({color:hc(col)}));
  m.position.set(x,y,z); sc.add(m); return m;
}
function mkCanvasMesh(sc,cvW,cvH,drawFn,wx,wy,ww,wh,z=0,transparent=true){
  const cv=document.createElement("canvas"); cv.width=cvW; cv.height=cvH;
  drawFn(cv.getContext("2d"),cvW,cvH);
  const tex=new THREE.CanvasTexture(cv);
  const m=new THREE.Mesh(new THREE.PlaneGeometry(ww,wh),
    new THREE.MeshBasicMaterial({map:tex,transparent}));
  m.position.set(wx,wy,z); sc.add(m);
  return {m,cv,ctx:cv.getContext("2d"),tex};
}

/* ─────────────────────────── AUDIO ────────────────────────────────────────── */
function mkAudio(){
  let ctx=null;
  const ac=()=>{
    if(!ctx) ctx=new(window.AudioContext||window.webkitAudioContext)();
    if(ctx.state==="suspended") ctx.resume();
    return ctx;
  };
  const noise=(dur,freq,gain,type="square")=>{
    const a=ac();
    const n=Math.ceil(a.sampleRate*dur),buf=a.createBuffer(1,n,a.sampleRate);
    const d=buf.getChannelData(0);
    for(let i=0;i<n;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/n,2.5);
    const src=a.createBufferSource(); src.buffer=buf;
    const lp=a.createBiquadFilter(); lp.type="lowpass"; lp.frequency.value=1400;
    const g=a.createGain();
    g.gain.setValueAtTime(gain,a.currentTime);
    g.gain.exponentialRampToValueAtTime(.001,a.currentTime+dur);
    src.connect(lp); lp.connect(g); g.connect(a.destination); src.start();
    const osc=a.createOscillator(),g2=a.createGain(); osc.type=type;
    osc.frequency.setValueAtTime(freq,a.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40,a.currentTime+dur*.6);
    g2.gain.setValueAtTime(gain*.4,a.currentTime);
    g2.gain.exponentialRampToValueAtTime(.001,a.currentTime+dur*.7);
    osc.connect(g2); g2.connect(a.destination);
    osc.start(); osc.stop(a.currentTime+dur*.7);
  };
  const tone=(t,f,dur,vol,type="square")=>{
    const a=ac();
    const osc=a.createOscillator(),g=a.createGain(); osc.type=type;
    osc.frequency.value=f;
    g.gain.setValueAtTime(vol,a.currentTime+t);
    g.gain.exponentialRampToValueAtTime(.001,a.currentTime+t+dur);
    osc.connect(g); g.connect(a.destination);
    osc.start(a.currentTime+t); osc.stop(a.currentTime+t+dur);
  };
  const shot  =()=>{ noise(.12,320,1.4); };
  const kill  =()=>{ tone(0,.00,520,.25); tone(0,.08,680,.22); tone(0,.16,880,.18); };
  const oops  =()=>{ tone(0,.00,440,.3,"sawtooth"); tone(0,.14,260,.28,"sawtooth"); };
  const miss  =()=>{ tone(0,.0,160,.15,"triangle"); };
  const escape=()=>{ [220,195,165,120].forEach((f,i)=>tone(0,i*.07,f,.2)); };
  const doorOpen=()=>{
    const a=ac();
    const o=a.createOscillator(),g=a.createGain();
    o.type="sine";
    o.frequency.setValueAtTime(180,a.currentTime);
    o.frequency.exponentialRampToValueAtTime(380,a.currentTime+.09);
    o.frequency.exponentialRampToValueAtTime(260,a.currentTime+.2);
    g.gain.setValueAtTime(0,a.currentTime);
    g.gain.linearRampToValueAtTime(.11,a.currentTime+.03);
    g.gain.exponentialRampToValueAtTime(.001,a.currentTime+.22);
    o.connect(g); g.connect(a.destination); o.start(); o.stop(a.currentTime+.22);
    const n=Math.ceil(a.sampleRate*.055),buf=a.createBuffer(1,n,a.sampleRate),d=buf.getChannelData(0);
    for(let i=0;i<n;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/n,1.8)*.035;
    const src=a.createBufferSource(); src.buffer=buf;
    const hp=a.createBiquadFilter(); hp.type="highpass"; hp.frequency.value=1600;
    const g2=a.createGain(); g2.gain.setValueAtTime(.45,a.currentTime);
    src.connect(hp); hp.connect(g2); g2.connect(a.destination); src.start();
  };
  return{shot,kill,oops,miss,escape,doorOpen};
}

/* ─────────────────────────── BACKGROUND ───────────────────────────────────── */
function buildScene(sc){

  /* Use the PNG background when the simplified graphics mode is enabled. */
  if(isPNGImagesActive){
    const bgTex = new THREE.TextureLoader().load(
      sfondoPngUrl,
      (loadedTex)=>{
        if(!loadedTex?.image?.width || !loadedTex?.image?.height) return;
        const targetAspect = LW / LH;
        const imageAspect = loadedTex.image.width / loadedTex.image.height;
        if(imageAspect > targetAspect){
          const repeatX = targetAspect / imageAspect;
          loadedTex.repeat.set(repeatX, 1);
          loadedTex.offset.set((1 - repeatX) / 2, 0);
        } else if(imageAspect < targetAspect){
          const repeatY = imageAspect / targetAspect;
          loadedTex.repeat.set(1, repeatY);
          loadedTex.offset.set(0, (1 - repeatY) / 2);
        } else {
          loadedTex.repeat.set(1, 1);
          loadedTex.offset.set(0, 0);
        }
        loadedTex.needsUpdate = true;
      }
    );
    bgTex.colorSpace = THREE.SRGBColorSpace;
    const bg = new THREE.Mesh(
      new THREE.PlaneGeometry(LW, LH),
      new THREE.MeshBasicMaterial({map:bgTex})
    );
    bg.position.set(0, 0, -20);
    sc.add(bg);
    return;
  }

  /* ═══════════════════════════════════════════════════════════════════
     FAR WEST  —  pixel-art style 1993 SNES/Genesis quality
     Reference: detailed chunky pixels, dithering, strong contrast,
     rich desert palette, wooden saloon architecture
  ═══════════════════════════════════════════════════════════════════ */

  const P = 3; // base pixel size

  /* ── pixel helpers ── */
  const snap = n => Math.round(n / P) * P;
  const pr = (c, x, y, w, h, col) => {
    c.fillStyle = col; c.fillRect(snap(x), snap(y), snap(w)||P, snap(h)||P);
  };

  /* chunky ellipse fill */
  const pe = (c, cx, cy, rx, ry, col) => {
    c.fillStyle = col;
    for(let dy = -ry; dy <= ry; dy += P) {
      const rowRx = rx * Math.sqrt(Math.max(0, 1 - (dy/ry)**2));
      const x0 = snap(cx - rowRx), w2 = snap(rowRx * 2);
      if(w2 > 0) c.fillRect(x0, snap(cy + dy), w2, P);
    }
  };

  /* dithering 2×2 pattern overlay */
  const dither = (c, x, y, w, h, col, alpha=.5) => {
    c.fillStyle = col;
    for(let dy = snap(y); dy < snap(y+h); dy += P*2)
      for(let dx = snap(x); dx < snap(x+w); dx += P*2)
        c.fillRect(dx, dy, P, P);
    for(let dy = snap(y)+P; dy < snap(y+h); dy += P*2)
      for(let dx = snap(x)+P; dx < snap(x+w); dx += P*2)
        c.fillRect(dx, dy, P, P);
  };

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     1.  SKY  — banded like SNES games
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, LH, (c, W, H) => {
    /* 6 flat colour bands — hard pixel steps */
    const bands = [
      [0,    .08,  "#1a2a7a"],
      [.08,  .18,  "#1e3ea0"],
      [.18,  .30,  "#2858c8"],
      [.30,  .42,  "#3878e0"],
      [.42,  .54,  "#5298f0"],
      [.54,  .62,  "#78bcf8"],
    ];
    bands.forEach(([a,b,col]) => {
      c.fillStyle = col;
      c.fillRect(0, snap(a*H), W, snap((b-a)*H)+2);
    });
    // dither blend at each band boundary
    bands.forEach(([a,b,col],i) => {
      if(i < bands.length-1) {
        const ny = snap(b*H);
        dither(c, 0, ny-P, W, P*3, bands[i+1][2], .5);
      }
    });
    // pale horizon wash
    const hz = c.createLinearGradient(0, H*.54, 0, H*.65);
    hz.addColorStop(0,"rgba(255,240,180,.0)");
    hz.addColorStop(1,"rgba(240,200,100,.22)");
    c.fillStyle=hz; c.fillRect(0,0,W,H);

    /* ── sun (pixel art, top-right) ── */
    const SX=snap(W*.78), SY=snap(H*.10);
    // outer glow halos — dithered rings
    [48,38,32].forEach((r,i) => {
      const glowCols=["rgba(255,240,160,.18)","rgba(255,220,100,.28)","rgba(255,200,60,.18)"];
      pe(c, SX, SY, r, r, glowCols[i]);
    });
    // sun disc — layered
    pe(c, SX, SY, 26, 26, "#fff060");
    pe(c, SX, SY, 22, 22, "#fff880");
    pe(c, SX, SY, 18, 18, "#ffffc0");
    // pixel rays — short blocky dashes
    const rayAngles = [0,45,90,135,180,225,270,315];
    rayAngles.forEach(deg => {
      const rad = deg * Math.PI/180;
      [34,44,54].forEach(r => {
        pr(c, SX+Math.cos(rad)*r-P, SY+Math.sin(rad)*r-P, P*3, P*3, "#ffe840");
      });
    });

    /* ── pixel art clouds ── */
    const cloud = (cx, cy, sc2=1) => {
      const blobes = [
        [0,   4,  30, 18, "#f0f8ff","#ffffff"],
        [-22, 10, 22, 14, "#e8f4ff","#f8ffff"],
        [24,  6,  28, 16, "#eef6ff","#ffffff"],
        [-8,  14, 44, 12, "#e0ecf8","#f0faff"],
        [8,   0,  20, 14, "#f8ffff","#ffffff"],
      ];
      blobes.forEach(([dx,dy,bw,bh,base,hi]) => {
        pr(c, cx+dx*sc2, cy+dy*sc2, bw*sc2, bh*sc2, base);
        pr(c, cx+(dx+2)*sc2, cy+(dy+2)*sc2, (bw-4)*sc2, (bh-6)*sc2, hi);
        // bottom shadow
        pr(c, cx+(dx+2)*sc2, cy+(dy+bh-P)*sc2, (bw-4)*sc2, P*2, "#b8cce0");
      });
    };
    cloud(snap(W*.09), snap(H*.06), 1.5);
    cloud(snap(W*.33), snap(H*.04), 1.2);
    cloud(snap(W*.56), snap(H*.08), 1.7);
    cloud(snap(W*.82), snap(H*.20), 1.0);

  }, 0, 0, LW, LH, -10, false);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     2.  DISTANT CANYON / MESA FORMATIONS
         Rich orange-red sandstone, SNES quality
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, 260, (c, W, H) => {
    c.clearRect(0, 0, W, H);

    // background haze — dither
    const haze = c.createLinearGradient(0, 0, 0, H);
    haze.addColorStop(0,"rgba(200,160,80,.12)");
    haze.addColorStop(1,"rgba(180,130,60,.0)");
    c.fillStyle=haze; c.fillRect(0,0,W,H);

    // mesa definitions — richer palette like reference image
    const mesas = [
      {x:  0, w:100, h:185, base:"#8b3a18", mid:"#a84a22", top:"#c05a28", lit:"#d87a40"},
      {x: 80, w: 70, h:110, base:"#7a3010", mid:"#9a4020", top:"#b05028", lit:"#c8703a"},
      {x:138, w:145, h:210, base:"#923c1a", mid:"#b04c24", top:"#c85e30", lit:"#e08048"},
      {x:262, w: 55, h: 90, base:"#7c3214", mid:"#9c4222", top:"#b25228", lit:"#ca7038"},
      {x:300, w:130, h:168, base:"#8e3e1c", mid:"#ac4e26", top:"#c45e30", lit:"#dc7e44"},
      {x:408, w: 80, h:120, base:"#823610", mid:"#a0461e", top:"#b85628", lit:"#d07438"},
      {x:470, w:190, h:220, base:"#963e1c", mid:"#b44e28", top:"#cc6034", lit:"#e4804c"},
      {x:638, w: 65, h: 98, base:"#7e3412", mid:"#9e4420", top:"#b45428", lit:"#cc7238"},
      {x:684, w:160, h:178, base:"#8c3c1a", mid:"#aa4c24", top:"#c25e30", lit:"#da7e44"},
      {x:820, w: 75, h:135, base:"#843812", mid:"#a2481e", top:"#ba5828", lit:"#d27640"},
      {x:876, w:100, h:155, base:"#903e1c", mid:"#ae4e26", top:"#c65e30", lit:"#de7e46"},
    ];

    mesas.forEach(({x, w, h, base, mid, top, lit}) => {
      // dark base
      pr(c, x, H-h, w, h, base);
      // mid section
      pr(c, x+P*2, H-h+P*4, w-P*4, h-P*8, mid);
      // light face (right side, lit by sun)
      pr(c, x+w*.6, H-h+P*4, w*.38, h-P*8, lit);
      dither(c, x+w*.45, H-h+P*4, w*.18, h-P*8, lit, .5);
      // top flat surface
      pr(c, x, H-h, w, P*4, top);
      pr(c, x+P, H-h+P, w-P*2, P*2, lighten(top, 24));
      // rocky notched silhouette
      for(let nx = x+P*3; nx < x+w-P*3; nx += P*5) {
        if((nx/P)%3 > 1) pr(c, nx, H-h-P*3, P*4, P*3, top);
      }
      // horizontal strata bands
      const strataH = [.25,.45,.65,.82];
      strataH.forEach(sy => {
        pr(c, x+P*2, H-h+h*sy, w-P*4, P*2, darken(base, 22));
        pr(c, x+P*2, H-h+h*sy+P*2, w-P*4, P, lighten(base, 12));
      });
      // left shadow face
      pr(c, x, H-h, P*3, h, darken(base, 30));
    });

    // desert floor — sandy transition
    const floor = c.createLinearGradient(0, H-40, 0, H);
    floor.addColorStop(0,"#d4922e"); floor.addColorStop(.5,"#c07a20"); floor.addColorStop(1,"#a86014");
    c.fillStyle=floor; c.fillRect(0, H-40, W, 40);
    // dither top of floor
    dither(c, 0, H-42, W, P*4, "#d4922e", .5);

  }, 0, -LH/2+118, -7, LW, 260, true);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     3.  DESERT GROUND — sandy, rocky, detailed
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, 240, (c, W, H) => {
    // base sand gradient
    const sg = c.createLinearGradient(0, 0, 0, H);
    sg.addColorStop(0,  "#c88a2a");
    sg.addColorStop(.2, "#b87420");
    sg.addColorStop(.55,"#a06016");
    sg.addColorStop(1,  "#885010");
    c.fillStyle=sg; c.fillRect(0,0,W,H);

    // pixel sand texture — scattered colour variations
    const sCols = ["#d49c38","#c48628","#b47020","#e0a840","#a85c18","#bc7a24","#d8a03a"];
    for(let i = 0; i < 300; i++) {
      const sx = snap(Math.random()*W), sy = snap(Math.random()*H);
      const sw = Math.random()>.6 ? P*2 : P;
      c.fillStyle = sCols[Math.floor(Math.random()*sCols.length)];
      c.fillRect(sx, sy, sw, P);
    }

    // dithered shadow at top edge
    dither(c, 0, 0, W, P*6, "#805010", .5);
    pr(c, 0, 0, W, P*2, "rgba(50,25,0,.4)");

    // ── old wooden fence ──
    const FY = H * .22;
    // rails first (behind posts)
    [FY+P*3, FY+P*9].forEach(ry => {
      for(let fx=0; fx<W; fx+=P*18) {
        pr(c, fx, ry, P*18+P*2, P*3, "#6a3e0e");
        pr(c, fx, ry+P, P*18+P*2, P, lighten("#6a3e0e", 18));
        pr(c, fx, ry+P*2, P*18+P*2, P, darken("#6a3e0e", 15));
        // nail
        pr(c, fx+P*2, ry+P, P*2, P*2, "#c09030");
      }
    });
    // posts
    for(let fx=0; fx<W; fx+=snap(W/18)) {
      pr(c, fx, FY-P*16, P*5, P*19+P*4, "#5a3408");
      pr(c, fx+P, FY-P*16, P*2, P*19+P*4, lighten("#5a3408",18));
      pr(c, fx+P*4, FY-P*16, P, P*19+P*4, darken("#5a3408",14));
      // pointed cap — 3 layers
      pr(c, fx+P, FY-P*19, P*3, P*3, "#5a3408");
      pr(c, fx+P*2, FY-P*22, P, P*3, "#5a3408");
    }

    // ── ground rocks (pixel chunky) ──
    const rocks = [
      {x:55,  y:H*.42, rx:18, ry:10, c1:"#7a5618",c2:"#9a7028"},
      {x:195, y:H*.68, rx:14, ry: 7, c1:"#6a4810",c2:"#8a6020"},
      {x:360, y:H*.35, rx:22, ry:12, c1:"#7e5a1c",c2:"#9e7030"},
      {x:490, y:H*.74, rx:16, ry: 9, c1:"#724c14",c2:"#926224"},
      {x:630, y:H*.48, rx:20, ry:10, c1:"#78541a",c2:"#987030"},
      {x:760, y:H*.62, rx:13, ry: 7, c1:"#6c4a12",c2:"#8c6022"},
      {x:880, y:H*.38, rx:17, ry: 9, c1:"#7c5618",c2:"#9c7028"},
    ];
    rocks.forEach(({x,y,rx,ry,c1,c2}) => {
      pe(c, x, y, rx, ry, c1);
      pe(c, x-rx*.1, y-ry*.3, rx*.65, ry*.55, c2);
      // lit top
      pr(c, snap(x-rx*.2), snap(y-ry*.6), snap(rx*.4), P*2, lighten(c2,22));
      // cast shadow
      dither(c, snap(x-rx*.8), snap(y+ry*.4), snap(rx*1.6), P*4, darken(c1,20), .5);
    });

    // ── tumbleweeds ──
    const tweed = (tx, ty, r) => {
      // shadow
      dither(c, snap(tx-r), snap(ty+r*.6), snap(r*2), P*3, darken("#a07828",22),.5);
      // stems
      c.strokeStyle="#9a7020"; c.lineWidth=P;
      [[0,-1],[.7,-.7],[1,0],[.7,.7],[0,1],[-.7,.7],[-1,0],[-.7,-.7]].forEach(([dx,dy])=>{
        c.beginPath();
        c.moveTo(snap(tx), snap(ty));
        c.lineTo(snap(tx+dx*r), snap(ty+dy*r)); c.stroke();
      });
      pe(c, tx, ty, r*.4, r*.4, "#c0922a");
      pe(c, tx, ty, r*.2, r*.2, "#e0b840");
    };
    tweed(230, H*.52, 14);
    tweed(695, H*.38, 11);
    tweed(840, H*.65, 9);

  }, 0, -LH/2+110, -5.5, LW, 240, false);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     4.  CACTUSES — detailed pixel art
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  const drawCactus = (c, cx, by, s=1) => {
    const B="#2a6018", M="#3a7828", L="#50a038", H2="#68c050", SP="#d4c060";
    const trunk_w = snap(14*s), arm_w = snap(10*s);
    const tall = snap(80*s);

    // shadow ellipse
    dither(c, cx-snap(20*s), by-P*3, snap(40*s), P*4, "rgba(0,0,0,.28)", .5);

    // trunk
    pr(c, cx-trunk_w/2, by-tall, trunk_w, tall, M);
    pr(c, cx-trunk_w/2+P, by-tall, snap(4*s), tall, L);  // light strip
    pr(c, cx+trunk_w/2-P*2, by-tall+P*4, P*2, tall-P*4, B); // right shadow
    // trunk top cap
    pr(c, cx-trunk_w/2+P, by-tall-P*3, trunk_w-P*2, P*3, L);
    pr(c, cx-trunk_w/2+snap(3*s), by-tall-P*5, snap(8*s), P*2, H2);

    // left arm
    const LA_Y = by-snap(52*s), LA_X = cx-trunk_w/2-snap(18*s);
    pr(c, LA_X, LA_Y, snap(18*s), arm_w, M);
    pr(c, LA_X, LA_Y+P, snap(18*s), P*2, L);
    pr(c, LA_X, LA_Y-snap(22*s), arm_w, snap(24*s), M);
    pr(c, LA_X+P, LA_Y-snap(22*s), snap(3*s), snap(24*s), L);
    pr(c, LA_X, LA_Y-snap(22*s)-P*3, arm_w-P, P*3, L);
    pr(c, LA_X+P, LA_Y-snap(22*s)-P*5, snap(5*s), P*2, H2);

    // right arm
    const RA_X = cx+trunk_w/2;
    const RA_Y = by-snap(38*s);
    pr(c, RA_X, RA_Y, snap(20*s), arm_w, M);
    pr(c, RA_X, RA_Y+P, snap(20*s), P*2, L);
    pr(c, RA_X+snap(10*s), RA_Y-snap(28*s), arm_w, snap(30*s), M);
    pr(c, RA_X+snap(10*s)+P, RA_Y-snap(28*s), snap(3*s), snap(28*s), L);
    pr(c, RA_X+snap(10*s), RA_Y-snap(28*s)-P*3, arm_w-P, P*3, L);
    pr(c, RA_X+snap(12*s), RA_Y-snap(28*s)-P*5, snap(5*s), P*2, H2);

    // spines
    const spinePos = [
      [cx-trunk_w/2-P*2, by-snap(28*s)],
      [cx-trunk_w/2-P*2, by-snap(50*s)],
      [cx+trunk_w/2,     by-snap(42*s)],
      [cx+trunk_w/2,     by-snap(20*s)],
      [LA_X-P*3,         LA_Y-snap(10*s)],
      [RA_X+snap(20*s),  RA_Y-snap(14*s)],
    ];
    spinePos.forEach(([sx,sy]) => {
      pr(c, sx-P*5, sy, P*5, P*2, SP);
      pr(c, sx, sy-P*4, P*2, P*5, SP);
    });
    // pixel dots at spine tips
    spinePos.forEach(([sx,sy]) => {
      pr(c, sx-P*5, sy, P, P, lighten(SP,20));
      pr(c, sx, sy-P*4, P, P, lighten(SP,20));
    });
  };

  mkCanvasMesh(sc, 220, 340, (c, W, H) => {
    c.clearRect(0,0,W,H);
    drawCactus(c, snap(W*.22), snap(H*.96), 1.4);
    drawCactus(c, snap(W*.72), snap(H*.98), 1.0);
  }, -LW/2+110, -LH/2+235, -4.8, 220, 340, true);

  mkCanvasMesh(sc, 220, 340, (c, W, H) => {
    c.clearRect(0,0,W,H);
    drawCactus(c, snap(W*.28), snap(H*.98), 1.1);
    drawCactus(c, snap(W*.76), snap(H*.95), 1.3);
  }, LW/2-110, -LH/2+235, -4.8, 220, 340, true);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     5.  SALOON FACADE  — detailed western wood
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, 440, (c, W, H) => {
    // Horizontal planks — 5 alternating shades
    const plankPal = ["#7a4c16","#885420","#6e4012","#96601e","#7e5018"];
    for(let py=0; py<H; py+=snap(18)) {
      const pi = Math.floor(py/snap(18)) % plankPal.length;
      const col = plankPal[pi];
      pr(c, 0, py, W, snap(16), col);
      pr(c, 0, py+P, W, P*2, lighten(col,20));
      pr(c, 0, py+snap(14), W, P*2, darken(col,22));
      // grain lines
      for(let gx=0; gx<W; gx+=snap(18+Math.floor(Math.random()*28))) {
        c.strokeStyle = darken(col,16);
        c.lineWidth = 1;
        c.beginPath(); c.moveTo(gx, py); c.lineTo(gx+P*2, py+snap(16)); c.stroke();
      }
      // plank knot
      if(Math.random()>.72) {
        const kx = snap(Math.random()*W*.85+W*.07);
        pe(c, kx, py+snap(8), snap(6), snap(4), darken(col,28));
        pe(c, kx, py+snap(8), snap(3), snap(2), darken(col,18));
      }
    }

    // ── large vertical support beams ──
    [18, 96, W/2-38, W/2+38, W-114, W-26].forEach(bx => {
      pr(c, bx, 0, snap(20), H, "#4a2c08");
      pr(c, bx+P*2, 0, P*4, H, lighten("#4a2c08",24));
      pr(c, bx+snap(16), 0, P*3, H, darken("#4a2c08",18));
      // metal bolts
      [H*.15, H*.4, H*.65, H*.88].forEach(by2 => {
        pr(c, bx+snap(6), by2, snap(8), snap(6), "#8a7040");
        pr(c, bx+snap(7), by2+P, P*2, P*2, "#c0a060");
      });
    });

    // ── top overhang / porch roof ──
    pr(c, 0, 0, W, snap(32), "#5a3410");
    // roof planks
    for(let px2=0; px2<W; px2+=snap(22)) {
      pr(c, px2, 0, snap(20), snap(28), "#6a4018");
      pr(c, px2+P*2, P, snap(16), snap(10), lighten("#6a4018",20));
    }
    pr(c, 0, 0, W, P*3, "#3a2008");
    pr(c, 0, P*2, W, P*3, "#8a5828");
    pr(c, 0, snap(28), W, P*4, "#2a1408");
    // decorative pixel zigzag
    for(let zx=0; zx<W; zx+=P*6) {
      pr(c, zx, snap(32), P*3, P*3, "#a07030");
      pr(c, zx+P*3, snap(35), P*3, P*3, "#8a5820");
    }

    // ── second floor railing ──
    const railY = snap(H*.18);
    pr(c, 0, railY, W, P*4, "#5a3810");
    pr(c, 0, railY+P, W, P*2, lighten("#5a3810",18));
    pr(c, 0, railY+P*3, W, P, darken("#5a3810",18));
    // baluster posts
    for(let bpx=P*4; bpx<W-P*4; bpx+=P*8) {
      pr(c, bpx, railY+P*4, P*4, snap(28), "#6a4418");
      pr(c, bpx+P, railY+P*4, P*2, snap(28), lighten("#6a4418",14));
    }
    pr(c, 0, railY+snap(32), W, P*4, "#5a3810");
    pr(c, 0, railY+snap(33), W, P*2, lighten("#5a3810",16));

    // ── windows on facade ──
    const wins = [
      {x:W*.10, y:snap(H*.28), w:snap(72), h:snap(88)},
      {x:W*.32, y:snap(H*.28), w:snap(72), h:snap(88)},
      {x:W*.60, y:snap(H*.28), w:snap(72), h:snap(88)},
      {x:W*.82, y:snap(H*.28), w:snap(72), h:snap(88)},
    ];
    wins.forEach(({x,y,w,h}) => {
      pr(c, x-P*3, y-P*3, w+P*6, h+P*6, "#3a2008");
      pr(c, x, y, w, h, "#0a1428");
      // glass glint
      pr(c, x+P*4, y+P*3, P*8, P*3, "rgba(180,220,255,.35)");
      pr(c, x+P*3, y+P*7, P*3, P*18, "rgba(160,200,255,.18)");
      // frame bars
      pr(c, x+w/2-P, y, P*2, h, "#4a2c10");
      pr(c, x, y+h/2-P, w, P*2, "#4a2c10");
      // window sill
      pr(c, x-P*4, y+h, w+P*8, P*5, "#6a4018");
      pr(c, x-P*4, y+h+P, w+P*8, P*2, lighten("#6a4018",20));
    });

  }, 0, LH/2-220, -5, LW, 440, false);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     6.  BANK SIGN  — carved wooden pixel sign
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, 520, 80, (c, W, H) => {
    // wooden planks
    ["#7a4e16","#8a5a20","#6e441c"].forEach((col,i) => {
      const sy = i * snap(H/3);
      pr(c, 0, sy, W, snap(H/3)-P, col);
      pr(c, 0, sy+P*2, W, P*3, lighten(col,22));
      pr(c, 0, sy+snap(H/3)-P*4, W, P*3, darken(col,22));
    });
    // thick border frame
    pr(c, 0, 0, W, P*4, "#3a2008");
    pr(c, 0, H-P*4, W, P*4, "#3a2008");
    pr(c, 0, 0, P*4, H, "#3a2008");
    pr(c, W-P*4, 0, P*4, H, "#3a2008");
    // gold inner frame
    pr(c, P*5, P*5, W-P*10, H-P*10, "rgba(0,0,0,0)");
    c.strokeStyle="#c09030"; c.lineWidth=P*2;
    c.strokeRect(P*6, P*6, W-P*12, H-P*12);
    // corner stars
    [[P*4,P*4],[W-P*10,P*4],[P*4,H-P*10],[W-P*10,H-P*10]].forEach(([nx,ny]) => {
      pr(c, nx, ny, P*6, P*6, "#d4a030");
      pr(c, nx+P*2, ny+P*2, P*2, P*2, "#ffe070");
    });
    // text with pixel shadow
    c.textAlign="center"; c.textBaseline="middle";
    c.font="bold 34px -apple-system,Arial,sans-serif";
    c.fillStyle="#3a1c04"; c.fillText("✦  THE BANK  ✦", W/2+3, H/2+3);
    c.fillStyle="#f0c030"; c.fillText("✦  THE BANK  ✦", W/2, H/2);
    c.fillStyle="rgba(255,240,100,.4)"; c.fillText("✦  THE BANK  ✦", W/2-1, H/2-1);
  }, 0, LH/2-32, -4, snap(520/3.6), snap(80/3.6), true);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     7.  HITCHING POSTS + LANTERNS
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  [-430, 430].forEach(sx => {
    // post
    mkCanvasMesh(sc, 22, 190, (c, W, H) => {
      pr(c, snap(W*.2), 0, snap(W*.6), H, "#5a3208");
      pr(c, snap(W*.28), 0, snap(W*.24), H, lighten("#5a3208",22));
      pr(c, snap(W*.72), 0, P*2, H, darken("#5a3208",18));
      [H*.22, H*.50, H*.78].forEach(ry => {
        pr(c, P, ry, W-P*2, P*5, "#3a1e04");
        pr(c, P*2, ry+P, W-P*4, P*2, "#8a5820");
      });
      pr(c, 0, 0, W, P*4, "#2a1404");
    }, sx, -LH/2+102, -4.6, 22, 190, true);

    // lantern body
    mkCanvasMesh(sc, 48, 56, (c, W, H) => {
      // hook
      pr(c, snap(W*.4), 0, P*5, P*8, "#4a3008");
      pr(c, snap(W*.42), P, P*3, P*6, lighten("#4a3008",14));
      // top cap
      pr(c, snap(W*.12), P*8, snap(W*.76), P*5, "#2e1c04");
      pr(c, snap(W*.18), P*8, snap(W*.64), P*3, "#5a3c10");
      pr(c, snap(W*.08), P*12, snap(W*.84), P*4, "#3a2408");
      // glass body
      pr(c, snap(W*.1), P*16, snap(W*.8), snap(H*.52), "#1a1008");
      // inner warm glow
      const lg = c.createRadialGradient(W/2, H*.42, P, W/2, H*.42, snap(W*.4));
      lg.addColorStop(0,"rgba(255,200,60,1)");
      lg.addColorStop(.4,"rgba(220,140,20,.6)");
      lg.addColorStop(.8,"rgba(160,80,0,.2)");
      lg.addColorStop(1,"rgba(0,0,0,0)");
      c.fillStyle=lg; c.fillRect(0,0,W,H);
      // frame bars
      pr(c, snap(W*.48), P*16, P*3, snap(H*.52), "#3a2408");
      pr(c, snap(W*.1), snap(H*.39), snap(W*.8), P*3, "#3a2408");
      // bottom cap
      pr(c, snap(W*.08), snap(H*.66), snap(W*.84), P*5, "#3a2408");
      pr(c, snap(W*.18), snap(H*.7), snap(W*.64), P*3, "#5a3c10");
      // small flame pixel
      pr(c, snap(W*.44), P*18, P*5, P*4, "#fff060");
      pr(c, snap(W*.46), P*17, P*3, P*2, "#ffffff");
    }, sx, -LH/2+196, -4.5, 48, 56, true);

    // warm light cone
    mkCanvasMesh(sc, 180, 130, (c, W, H) => {
      const g = c.createRadialGradient(W/2, 0, P*2, W/2, 0, 120);
      g.addColorStop(0,"rgba(255,200,60,.28)");
      g.addColorStop(.3,"rgba(220,150,30,.16)");
      g.addColorStop(.6,"rgba(180,100,0,.07)");
      g.addColorStop(1,"rgba(0,0,0,0)");
      c.fillStyle=g; c.fillRect(0,0,W,H);
      dither(c, W*.1, P*4, W*.8, P*8, "rgba(255,200,80,.14)", .5);
    }, sx, -LH/2+118, -4.4, 180, 130, true);
  });

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     8.  FRONT DIRT STRIP
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, 44, (c, W, H) => {
    pr(c, 0, 0, W, H, "#a07030");
    const dCols=["#b88040","#906020","#c89040","#a07030"];
    for(let i=0;i<180;i++) {
      const dx=snap(Math.random()*W),dy=snap(Math.random()*H);
      c.fillStyle=dCols[Math.floor(Math.random()*dCols.length)];
      c.fillRect(dx,dy,P,P);
    }
    // front shadow
    const es=c.createLinearGradient(0,0,0,H);
    es.addColorStop(0,"rgba(0,0,0,.38)"); es.addColorStop(.5,"rgba(0,0,0,0)");
    c.fillStyle=es; c.fillRect(0,0,W,H);
  }, 0, -LH/2+133, -5.2, LW, 44, false);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     9.  VIGNETTE
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  mkCanvasMesh(sc, LW, LH, (c, W, H) => {
    const g = c.createRadialGradient(W/2,H/2,W*.18,W/2,H/2,W*.78);
    g.addColorStop(0,"rgba(0,0,0,0)");
    g.addColorStop(.7,"rgba(4,2,0,.28)");
    g.addColorStop(1,"rgba(12,6,0,.80)");
    c.fillStyle=g; c.fillRect(0,0,W,H);
  }, 0, 0, LW, LH, 6.5, true);
}

/* ─────────────────────────── DOOR ─────────────────────────────────────────── */
// Draws a door canvas for open/closed/boom states, optionally using the PNG image.
function drawDoor(open,boom,di){
  const W=DW,H=DH;
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H;
  const c=cv.getContext("2d");
  const ACCENTS=["#ff4444","#f0a030","#34d880"];
  const ac=ACCENTS[di];

  if(isPNGImagesActive && doorPngReady && doorPngImage){
    c.drawImage(doorPngImage,0,0,W,H);
    return cv;
  }

  if(boom){
    /* scorched / shattered */
    const bg=c.createLinearGradient(0,0,W,H);
    bg.addColorStop(0,"#180a04"); bg.addColorStop(1,"#0c0608");
    c.fillStyle=bg; c.fillRect(0,0,W,H);
    /* scorch center */
    const sg=c.createRadialGradient(W*.5,H*.44,4,W*.5,H*.44,W*.45);
    sg.addColorStop(0,"rgba(255,120,0,.6)");
    sg.addColorStop(.4,"rgba(120,30,0,.35)");
    sg.addColorStop(1,"rgba(0,0,0,0)");
    c.fillStyle=sg; c.fillRect(0,0,W,H);
    /* fracture lines */
    c.strokeStyle="rgba(255,90,0,.7)"; c.lineWidth=2;
    [[W*.5,H*.2,W*.15,H*.92],[W*.28,H*.08,W*.75,H*.85],[W*.72,H*.12,W*.18,H*.96]].forEach(([x1,y1,x2,y2])=>{
      c.beginPath(); c.moveTo(x1,y1); c.lineTo(x2,y2); c.stroke();
    });
    /* debris */
    for(let i=0;i<12;i++){
      const x=W*(.15+Math.random()*.7), y=H*(.15+Math.random()*.75);
      c.fillStyle=["#7a2a10","#5a1a08","#9a3a18"][i%3];
      c.beginPath(); c.arc(x,y,2+Math.random()*5,0,Math.PI*2); c.fill();
    }
    c.strokeStyle=ac; c.lineWidth=2; c.globalAlpha=.35;
    c.strokeRect(4,4,W-8,H-8); c.globalAlpha=1;
    return cv;
  }

  /* ── frame ── */
  const fg=c.createLinearGradient(0,0,W,0);
  fg.addColorStop(0,"#141428"); fg.addColorStop(.15,"#22223c");
  fg.addColorStop(.5,"#2e2e4e"); fg.addColorStop(.85,"#22223c"); fg.addColorStop(1,"#141428");
  c.fillStyle=fg; c.fillRect(0,0,W,H);

  /* outer neon border */
  c.shadowColor=ac; c.shadowBlur=open?22:12;
  c.strokeStyle=ac; c.lineWidth=open?3:2;
  c.strokeRect(3,3,W-6,H-6);
  c.shadowBlur=0;

  /* inner secondary border */
  c.strokeStyle="rgba(255,255,255,.07)"; c.lineWidth=1;
  c.strokeRect(8,8,W-16,H-16);

  /* corner ornaments */
  [[6,6],[W-22,6],[6,H-22],[W-22,H-22]].forEach(([ox,oy])=>{
    c.shadowColor=ac; c.shadowBlur=6;
    c.strokeStyle=ac; c.lineWidth=2;
    c.beginPath(); c.moveTo(ox,oy+12); c.lineTo(ox,oy); c.lineTo(ox+12,oy); c.stroke();
    c.shadowBlur=0;
  });

  /* arch */
  const archY=H*.35, archR=(W-22)*.5;
  c.beginPath(); c.arc(W/2,archY,archR,Math.PI,0);
  c.lineTo(W-11,H-14); c.lineTo(11,H-14); c.closePath();

  if(open){
    /* interior */
    const ig=c.createRadialGradient(W/2,H*.5,8,W/2,H*.5,W*.6);
    ig.addColorStop(0,"#130c08"); ig.addColorStop(.5,"#0c0808"); ig.addColorStop(1,"#060406");
    c.fillStyle=ig; c.fill();

    /* floor tiles */
    c.save(); c.clip();
    c.strokeStyle="rgba(80,50,20,.28)"; c.lineWidth=1;
    for(let ty=H*.68;ty<H-14;ty+=20){
      c.beginPath(); c.moveTo(11,ty); c.lineTo(W-11,ty); c.stroke();
    }
    for(let tx=11;tx<W-11;tx+=28){
      c.beginPath(); c.moveTo(tx,H*.68); c.lineTo(tx,H-14); c.stroke();
    }
    /* warm interior glow */
    const wg=c.createRadialGradient(W/2,H*.38,5,W/2,H*.38,W*.44);
    wg.addColorStop(0,"rgba(255,140,40,.06)"); wg.addColorStop(1,"rgba(0,0,0,0)");
    c.fillStyle=wg; c.fillRect(0,0,W,H);
    c.restore();

    /* door leaves (perspective) */
    [11,W-42].forEach((px,si)=>{
      const dg=c.createLinearGradient(px,0,px+30,0);
      if(si===0){ dg.addColorStop(0,"#3e281a"); dg.addColorStop(1,"#120c08"); }
      else { dg.addColorStop(0,"#120c08"); dg.addColorStop(1,"#3e281a"); }
      c.fillStyle=dg; c.fillRect(px,archY-2,30,H-archY-12);
      /* grain */
      c.strokeStyle="rgba(0,0,0,.25)"; c.lineWidth=1;
      for(let gx=px+6;gx<px+26;gx+=7){
        c.beginPath(); c.moveTo(gx,archY); c.lineTo(gx+1,H-14); c.stroke();
      }
    });

    /* arch glow line */
    c.shadowColor=ac; c.shadowBlur=24;
    c.strokeStyle=ac; c.lineWidth=2.5;
    c.beginPath(); c.arc(W/2,archY,archR,Math.PI,0); c.stroke();
    c.shadowBlur=0;

  } else {
    /* closed door – dark wood */
    const dg=c.createLinearGradient(11,0,W-11,0);
    dg.addColorStop(0,"#1c1008"); dg.addColorStop(.25,"#2c1c10");
    dg.addColorStop(.5,"#341e12"); dg.addColorStop(.75,"#261610"); dg.addColorStop(1,"#181008");
    c.fillStyle=dg; c.fill();

    /* wood grain */
    c.strokeStyle="rgba(0,0,0,.18)"; c.lineWidth=1.2;
    for(let gx=14;gx<W-12;gx+=9){
      c.beginPath(); c.moveTo(gx,archY-2+Math.sin(gx*.4)*2); c.lineTo(gx+.8,H-14); c.stroke();
    }

    /* raised panels */
    [[W*.08,archY+10,W*.38,H*.23],[W*.54,archY+10,W*.38,H*.23],
     [W*.08,archY+H*.31,W*.38,H*.31],[W*.54,archY+H*.31,W*.38,H*.31]].forEach(([px,py,pw,ph])=>{
      c.fillStyle="rgba(0,0,0,.28)"; c.fillRect(px,py,pw,ph);
      /* bevel highlight */
      c.fillStyle="rgba(255,255,255,.05)"; c.fillRect(px+2,py+2,pw-4,ph*.45);
      c.strokeStyle="rgba(0,0,0,.45)"; c.lineWidth=1.5; c.strokeRect(px,py,pw,ph);
      c.strokeStyle="rgba(255,255,255,.07)"; c.lineWidth=1; c.strokeRect(px+2,py+2,pw-4,ph-4);
    });

    /* handle */
    c.shadowColor="#ffd060"; c.shadowBlur=10;
    c.fillStyle="#b88010";
    c.beginPath(); c.arc(W*.63,H*.555,8,0,Math.PI*2); c.fill();
    c.fillStyle="#ffd060";
    c.beginPath(); c.arc(W*.63,H*.555,5,0,Math.PI*2); c.fill();
    c.fillStyle="#c09010"; c.fillRect(W*.60,H*.555+7,8,16);
    c.shadowBlur=0;

    /* door number plate */
    const numCv=document.createElement("canvas"); numCv.width=48; numCv.height=32;
    const nc=numCv.getContext("2d");
    nc.fillStyle="rgba(0,0,0,.75)"; nc.fillRect(0,0,48,32);
    nc.strokeStyle=ac; nc.lineWidth=1.5; nc.strokeRect(1.5,1.5,45,29);
    nc.fillStyle=ac; nc.font="bold 15px monospace";
    nc.textAlign="center"; nc.textBaseline="middle";
    nc.fillText(["I","II","III"][di],24,16);
    c.drawImage(numCv,W*.5-24,H*.445);

    /* bottom vignette */
    const sv=c.createLinearGradient(0,H*.85,0,H-14);
    sv.addColorStop(0,"rgba(0,0,0,0)"); sv.addColorStop(1,"rgba(0,0,0,.5)");
    c.fillStyle=sv; c.fillRect(11,H*.85,W-22,H*.15-14);
  }

  /* threshold bar */
  const thg=c.createLinearGradient(0,H-14,0,H-2);
  thg.addColorStop(0,"#2e2e50"); thg.addColorStop(1,"#1a1a38");
  c.fillStyle=thg; c.fillRect(4,H-14,W-8,12);
  c.shadowColor=ac; c.shadowBlur=8;
  c.strokeStyle=ac; c.lineWidth=1.5;
  c.beginPath(); c.moveTo(4,H-14); c.lineTo(W-4,H-14); c.stroke();
  c.shadowBlur=0;

  return cv;
}

/* ─────────────────────────── CHARACTER ────────────────────────────────────── */
function drawChar(type,idx){
  const CW=148,CH=256;
  const cv=document.createElement("canvas"); cv.width=CW; cv.height=CH;
  const c=cv.getContext("2d");
  const isB=type==="bandit";

  // ── 9 client palettes (idx 0-8) ─────────────────────────────────────────
  // idx 8 is the "decoy" — all-black suit, dark tie, neutral face (looks suspicious)
  const suitPalette=[
    // 0 classic navy
    {j:"#1e3a72",p:"#142a54",s:"#2a5298",tie:"#c01818",eyes:"#3040a0",hair:"#1c1408",skin:"#c8a070",bc:"#8a6030"},
    // 1 forest green
    {j:"#1a5224",p:"#0e3818",s:"#267a34",tie:"#e8c020",eyes:"#208040",hair:"#5a3018",skin:"#d4a870",bc:"#7a5020"},
    // 2 royal purple
    {j:"#4a1e72",p:"#2e1250",s:"#6a2a92",tie:"#e040c0",eyes:"#9030c0",hair:"#0c0c0c",skin:"#c89060",bc:"#6a4810"},
    // 3 burgundy / wine
    {j:"#6a1020",p:"#4a0a14",s:"#8a1828",tie:"#f0a020",eyes:"#803030",hair:"#2a1008",skin:"#c8a070",bc:"#8a6030"},
    // 4 camel / tan
    {j:"#9a7840",p:"#7a5828",s:"#b89050",tie:"#1a6090",eyes:"#607040",hair:"#1c1008",skin:"#d4a876",bc:"#704810"},
    // 5 slate grey
    {j:"#3a4050",p:"#282e38",s:"#505868",tie:"#e04040",eyes:"#4060a0",hair:"#888888",skin:"#b89060",bc:"#806040"},
    // 6 cobalt blue + orange tie
    {j:"#1030a0",p:"#0a2070",s:"#1848c0",tie:"#f07010",eyes:"#2060e0",hair:"#2a1a0a",skin:"#c8a870",bc:"#8a6420"},
    // 7 olive / military green
    {j:"#4a5020",p:"#323514",s:"#606828",tie:"#c04030",eyes:"#507030",hair:"#181008",skin:"#c0a060",bc:"#705020"},
    // 8 DECOY — all black suit, dark charcoal, no smile, poker face
    {j:"#0e0e0e",p:"#0a0a0a",s:"#161616",tie:"#1a1a22",eyes:"#303040",hair:"#080808",skin:"#b89060",bc:"#5a3a10",
     decoy:true},
  ];

  const maskPalette=[
    {suit:"#0e0e14",acc:"#ff2222",laser:"#ff0000"},
    {suit:"#181008",acc:"#ff8800",laser:"#ff6600"},
    {suit:"#080e18",acc:"#2244ff",laser:"#0033ff"},
  ];

  const pal=isB ? maskPalette[idx%3] : suitPalette[idx%9];
  const skin=pal.skin||"#c8a070";
  const isDecoy=!isB && !!pal.decoy;

  c.clearRect(0,0,CW,CH);

  /* shadow */
  const shg=c.createRadialGradient(CW/2,CH-10,2,CW/2,CH-10,46);
  shg.addColorStop(0,"rgba(0,0,0,.55)"); shg.addColorStop(1,"rgba(0,0,0,0)");
  c.fillStyle=shg; c.beginPath(); c.ellipse(CW/2,CH-10,46,13,0,0,Math.PI*2); c.fill();

  /* shoes */
  const shoeCol=isDecoy?"#060606":"#0a0a0a";
  [[CW*.29,CH*.88,-.12],[CW*.71,CH*.88,.12]].forEach(([ex,ey,ang])=>{
    c.fillStyle=shoeCol;
    c.beginPath(); c.ellipse(ex,ey,18,9,ang,0,Math.PI*2); c.fill();
    c.fillStyle=isDecoy?"#0e0e0e":"#1a1a1a";
    c.beginPath(); c.ellipse(ex,ey-2,15,7,ang*.8,0,Math.PI*2); c.fill();
  });

  /* trousers */
  const tg=c.createLinearGradient(CW*.2,0,CW*.8,0);
  const pc=pal.p||pal.suit;
  tg.addColorStop(0,pc); tg.addColorStop(.5,lighten(pc,isDecoy?6:20)); tg.addColorStop(1,pc);
  c.fillStyle=tg;
  [[CW*.21,CH*.54,CW*.23,CH*.36],[CW*.56,CH*.54,CW*.23,CH*.36]].forEach(([x,y,w,h])=>{
    c.beginPath(); c.roundRect(x,y,w,h,4); c.fill();
    c.strokeStyle="rgba(255,255,255,.08)"; c.lineWidth=1.5;
    c.beginPath(); c.moveTo(x+w/2,y); c.lineTo(x+w/2+1,y+h); c.stroke();
  });

  /* jacket */
  const jg=c.createLinearGradient(CW*.1,0,CW*.9,0);
  const jc=pal.j||pal.suit;
  jg.addColorStop(0,jc); jg.addColorStop(.4,lighten(jc,isDecoy?8:25)); jg.addColorStop(1,jc);
  c.fillStyle=jg;
  c.beginPath(); c.roundRect(CW*.12,CH*.27,CW*.76,CH*.3,5); c.fill();

  /* lapels */
  [[CW*.37,CW*.5,CW*.32],[CW*.63,CW*.5,CW*.68]].forEach(([sx,mx,ex],i)=>{
    c.fillStyle="rgba(255,255,255,.06)";
    c.beginPath();
    if(i===0){ c.moveTo(sx,CH*.27); c.lineTo(mx,CH*.41); c.lineTo(ex,CH*.57); c.lineTo(CW*.2,CH*.57); c.lineTo(CW*.2,CH*.27); }
    else      { c.moveTo(ex,CH*.27); c.lineTo(mx,CH*.41); c.lineTo(CW*.8,CH*.57); c.lineTo(CW*.8,CH*.57); c.lineTo(CW*.8,CH*.27); }
    c.closePath(); c.fill();
  });

  if(!isB){
    /* tie */
    const tc=pal.tie;
    const tiG=c.createLinearGradient(CW*.46,CH*.29,CW*.54,CH*.62);
    tiG.addColorStop(0,tc); tiG.addColorStop(.5,lighten(tc,isDecoy?4:28)); tiG.addColorStop(1,tc);
    c.fillStyle=tiG;
    c.beginPath(); c.moveTo(CW*.46,CH*.3); c.lineTo(CW*.54,CH*.3);
    c.lineTo(CW*.57,CH*.56); c.lineTo(CW*.5,CH*.63); c.lineTo(CW*.43,CH*.56); c.closePath(); c.fill();
    /* tie pin — small detail */
    if(!isDecoy){
      c.fillStyle="rgba(255,220,80,.8)"; c.fillRect(CW*.485,CH*.44,CW*.03,3);
    }
    /* pocket square */
    if(!isDecoy){
      c.fillStyle="rgba(255,255,255,.75)"; c.fillRect(CW*.225,CH*.31,8,5);
      c.fillStyle=tc; c.fillRect(CW*.226,CH*.3,7,4);
    }
    /* briefcase */
    const bcc=pal.bc||"#7a5420";
    const bcg=c.createLinearGradient(CW*.82,0,CW*.82+26,0);
    bcg.addColorStop(0,bcc); bcg.addColorStop(.5,lighten(bcc,18)); bcg.addColorStop(1,bcc);
    c.fillStyle=bcg; c.beginPath(); c.roundRect(CW*.82,CH*.5,26,20,3); c.fill();
    c.strokeStyle=darken(bcc,18); c.lineWidth=1.5;
    c.beginPath(); c.roundRect(CW*.82,CH*.5,26,20,3); c.stroke();
    c.fillStyle=lighten(bcc,22); c.fillRect(CW*.87,CH*.475,16,6);
    c.strokeStyle=darken(bcc,18); c.lineWidth=1;
    c.beginPath(); c.roundRect(CW*.87,CH*.475,16,6,2); c.stroke();
    /* watch — only non-decoy */
    if(!isDecoy){
      c.fillStyle="#888"; c.beginPath(); c.arc(CW*.862,CH*.53,5,0,Math.PI*2); c.fill();
      c.fillStyle="#ddd"; c.beginPath(); c.arc(CW*.862,CH*.53,3,0,Math.PI*2); c.fill();
    }
  } else {
    /* tactical belt */
    c.fillStyle="#080808"; c.fillRect(CW*.12,CH*.535,CW*.76,12);
    c.fillStyle="#404040"; c.fillRect(CW*.43,CH*.535,18,12);
    c.strokeStyle="#686868"; c.lineWidth=1; c.strokeRect(CW*.43,CH*.535,18,12);
    /* holster */
    c.fillStyle="#111"; c.beginPath(); c.roundRect(CW*.68,CH*.52,18,28,3); c.fill();
    c.strokeStyle="#333"; c.lineWidth=1; c.beginPath(); c.roundRect(CW*.68,CH*.52,18,28,3); c.stroke();
  }

  /* arms */
  const ag=c.createLinearGradient(0,CH*.28,0,CH*.6);
  ag.addColorStop(0,jc||pal.suit); ag.addColorStop(1,darken(jc||pal.suit,20));
  c.fillStyle=ag;
  if(isB){
    c.beginPath(); c.roundRect(CW*.01,CH*.27,CW*.14,CH*.3,4); c.fill();
    c.beginPath(); c.roundRect(CW*.84,CH*.14,CW*.14,CH*.32,4); c.fill();
    /* pistol */
    c.fillStyle="#181818";
    c.beginPath(); c.roundRect(CW*.88,CH*.1,38,14,3); c.fill();
    c.beginPath(); c.roundRect(CW*.9,CH*.23,16,20,3); c.fill();
    c.fillStyle="#242424"; c.fillRect(CW*.88,CH*.12,30,7);
    c.fillStyle="#0e0e0e"; c.fillRect(CW*.88,CH*.23,7,8);
    c.fillStyle="#121212"; c.beginPath(); c.roundRect(CW+8,CH*.1,12,10,3); c.fill();
    c.fillStyle=pal.laser; c.shadowColor=pal.laser; c.shadowBlur=8;
    c.beginPath(); c.arc(CW+24,CH*.15,3,0,Math.PI*2); c.fill();
    c.shadowBlur=0;
    c.strokeStyle=pal.laser+"88"; c.lineWidth=1; c.setLineDash([4,4]);
    c.beginPath(); c.moveTo(CW+24,CH*.15); c.lineTo(CW+60,CH*.15); c.stroke();
    c.setLineDash([]);
  } else {
    c.beginPath(); c.roundRect(CW*.01,CH*.27,CW*.14,CH*.32,4); c.fill();
    c.beginPath(); c.roundRect(CW*.85,CH*.27,CW*.14,CH*.32,4); c.fill();
  }

  /* cuffs */
  c.fillStyle=isDecoy?"rgba(80,80,80,.2)":"rgba(255,255,255,.18)";
  c.fillRect(CW*.01,CH*.55,CW*.14,7);
  c.fillRect(CW*.85,isB?CH*.43:CH*.55,CW*.14,7);

  /* hands */
  c.fillStyle=skin;
  c.beginPath(); c.ellipse(CW*.085,CH*.575,7,9,0,0,Math.PI*2); c.fill();
  c.beginPath(); c.ellipse(CW*.915,isB?CH*.47:CH*.575,7,9,0,0,Math.PI*2); c.fill();

  /* neck */
  c.fillStyle=skin; c.fillRect(CW*.42,CH*.185,CW*.16,CH*.1);

  /* head */
  const hg=c.createRadialGradient(CW*.5,CH*.135,2,CW*.5,CH*.135,28);
  hg.addColorStop(0,skin); hg.addColorStop(1,darken(skin,20));
  c.fillStyle=hg;
  c.beginPath(); c.ellipse(CW*.5,CH*.135,CW*.215,CH*.145,0,0,Math.PI*2); c.fill();

  if(isB){
    /* balaclava */
    c.fillStyle=pal.suit;
    c.beginPath(); c.ellipse(CW*.5,CH*.135,CW*.22,CH*.148,0,0,Math.PI*2); c.fill();
    [[CW*.35,CH*.12],[CW*.63,CH*.12]].forEach(([ex,ey])=>{
      c.strokeStyle=darken(pal.acc,10); c.lineWidth=3;
      c.beginPath(); c.ellipse(ex,ey,10,8,.15,0,Math.PI*2); c.stroke();
      const lg=c.createRadialGradient(ex,ey,1,ex,ey,9);
      lg.addColorStop(0,pal.acc+"ee"); lg.addColorStop(.5,pal.acc+"88"); lg.addColorStop(1,"rgba(0,0,0,.5)");
      c.fillStyle=lg; c.shadowColor=pal.acc; c.shadowBlur=12;
      c.beginPath(); c.ellipse(ex,ey,9,7,.1,0,Math.PI*2); c.fill();
      c.shadowBlur=0;
      c.fillStyle="rgba(255,255,255,.25)";
      c.beginPath(); c.ellipse(ex-3,ey-2,4,2.5,-.5,0,Math.PI*2); c.fill();
    });
    c.strokeStyle="#222"; c.lineWidth=2.5;
    c.beginPath(); c.moveTo(CW*.44,CH*.12); c.lineTo(CW*.55,CH*.12); c.stroke();
    c.fillStyle="#1a1a1a"; c.fillRect(CW*.37,CH*.19,CW*.26,5);
    c.strokeStyle="rgba(255,255,255,.06)"; c.lineWidth=1;
    for(let mx=CW*.37+4;mx<CW*.63;mx+=5){
      c.beginPath(); c.moveTo(mx,CH*.19); c.lineTo(mx,CH*.19+5); c.stroke();
    }
    c.strokeStyle=pal.acc+"33"; c.lineWidth=1; c.setLineDash([3,3]);
    c.strokeRect(CW*.14,CH*.31,CW*.72,CH*.23); c.setLineDash([]);
    c.fillStyle=pal.acc; c.shadowColor=pal.acc; c.shadowBlur=7;
    c.font="700 11px 'SF Pro','Helvetica Neue',sans-serif";
    c.textAlign="center"; c.fillText("BANDIT",CW*.5,CH*.28);
    c.shadowBlur=0;
  } else {
    /* hair */
    const hcol=pal.hair;
    c.fillStyle=hcol;
    c.beginPath(); c.ellipse(CW*.5,CH*.062,CW*.22,CH*.072,0,0,Math.PI); c.fill();
    c.fillRect(CW*.28,CH*.068,7,16); c.fillRect(CW*.65,CH*.068,7,16);
    /* ears */
    c.fillStyle=skin;
    c.beginPath(); c.ellipse(CW*.28,CH*.135,5,7,0,0,Math.PI*2); c.fill();
    c.beginPath(); c.ellipse(CW*.72,CH*.135,5,7,0,0,Math.PI*2); c.fill();
    /* eyes */
    c.fillStyle="#fff";
    [[CW*.37,CH*.13],[CW*.63,CH*.13]].forEach(([ex,ey])=>{
      c.beginPath(); c.ellipse(ex,ey,6,5,0,0,Math.PI*2); c.fill();
      c.fillStyle=pal.eyes; c.beginPath(); c.arc(ex,ey,3.5,0,Math.PI*2); c.fill();
      c.fillStyle="#111"; c.beginPath(); c.arc(ex,ey,1.8,0,Math.PI*2); c.fill();
      c.fillStyle="rgba(255,255,255,.55)"; c.beginPath(); c.arc(ex-1.5,ey-1.5,1.2,0,Math.PI*2); c.fill();
      c.fillStyle="#fff";
    });
    /* eyebrows */
    c.strokeStyle=hcol; c.lineWidth=isDecoy?2.8:2.2; c.lineCap="round";
    // decoy has slightly furrowed brows
    if(isDecoy){
      c.beginPath(); c.moveTo(CW*.3,CH*.105); c.quadraticCurveTo(CW*.37,CH*.088,CW*.445,CH*.102); c.stroke();
      c.beginPath(); c.moveTo(CW*.555,CH*.102); c.quadraticCurveTo(CW*.63,CH*.088,CW*.7,CH*.105); c.stroke();
    } else {
      c.beginPath(); c.moveTo(CW*.3,CH*.1); c.quadraticCurveTo(CW*.37,CH*.085,CW*.445,CH*.1); c.stroke();
      c.beginPath(); c.moveTo(CW*.555,CH*.1); c.quadraticCurveTo(CW*.63,CH*.085,CW*.7,CH*.1); c.stroke();
    }
    /* nose */
    c.strokeStyle="rgba(0,0,0,.2)"; c.lineWidth=1; c.lineCap="round";
    c.beginPath(); c.moveTo(CW*.5,CH*.15); c.lineTo(CW*.47,CH*.18); c.stroke();
    c.beginPath(); c.moveTo(CW*.5,CH*.15); c.lineTo(CW*.53,CH*.18); c.stroke();
    /* expression */
    c.strokeStyle="rgba(120,70,30,.45)"; c.lineWidth=1.5;
    if(isDecoy){
      // neutral / straight mouth
      c.beginPath(); c.moveTo(CW*.41,CH*.175); c.lineTo(CW*.59,CH*.175); c.stroke();
      // sweat drop
      c.fillStyle="rgba(100,160,255,.55)";
      c.beginPath(); c.arc(CW*.72,CH*.1,3,0,Math.PI*2); c.fill();
      c.beginPath();
      c.moveTo(CW*.72,CH*.07); c.quadraticCurveTo(CW*.74,CH*.09,CW*.72,CH*.1); c.closePath();
      c.fill();
    } else {
      c.beginPath(); c.arc(CW*.5,CH*.167,9,.05,Math.PI-.05); c.stroke();
    }
    /* id badge */
    c.fillStyle=isDecoy?"rgba(20,20,20,.8)":"rgba(255,255,255,.07)";
    c.beginPath(); c.roundRect(CW*.22,CH*.385,38,15,3); c.fill();
    if(isDecoy){
      c.strokeStyle="rgba(255,255,255,.15)"; c.lineWidth=1;
      c.beginPath(); c.roundRect(CW*.22,CH*.385,38,15,3); c.stroke();
    }
    c.fillStyle=isDecoy?"rgba(255,255,255,.3)":"rgba(255,255,255,.45)";
    c.font="8px monospace"; c.textAlign="left";
    c.fillText(isDecoy?"VIP-??":`CLT-${(idx%8)+1}`,CW*.235,CH*.445);
  }

  return cv;
}

function lighten(hex,amt){
  if(typeof hex!=="string") hex="#"+Math.abs(hex).toString(16).padStart(6,"0");
  const n=parseInt(hex.replace(/^#/,""),16)||0;
  const clamp=(v)=>Math.max(0,Math.min(255,v));
  const r=clamp(((n>>16)&255)+amt), g=clamp(((n>>8)&255)+amt), b=clamp((n&255)+amt);
  return `#${r.toString(16).padStart(2,"0")}${g.toString(16).padStart(2,"0")}${b.toString(16).padStart(2,"0")}`;
}
function darken(hex,amt){ return lighten(hex,-amt); }

/* ─────────────────────────── EXPLOSION ────────────────────────────────────── */
const ECOLS={
  bandit:["#ff4400","#ff8800","#ffdd00","#ffffff","#ff2200","#ff6600","#ffaa00"],
  client:["#2266ff","#66aaff","#ffffff","#ff6688","#88bbff","#4488ff"],
  miss:  ["#555570","#7070a0","#9090b8","#404058","#606080"],
};
function spawnExp(sc,x,y,type,scale=1){
  const cols=ECOLS[type]||ECOLS.miss, parts=[];
  const n=Math.round(44*scale);
  for(let i=0;i<n;i++){
    const ang=(i/n)*Math.PI*2+(Math.random()-.5)*.6;
    const sp=(65+Math.random()*140)*scale;
    const sz=(3+Math.random()*15)*scale;
    const col=cols[Math.floor(Math.random()*cols.length)];
    const life=.38+Math.random()*.38;
    const cv2=document.createElement("canvas"); cv2.width=22; cv2.height=22;
    const t2=new THREE.CanvasTexture(cv2);
    const shapes=["rect","star","ring","spark"];
    const sh=shapes[Math.floor(Math.random()*shapes.length)];
    const m=new THREE.Mesh(new THREE.PlaneGeometry(sz/10,sz/10),
      new THREE.MeshBasicMaterial({map:t2,transparent:true,depthWrite:false}));
    m.position.set(x,y,3); sc.add(m);
    parts.push({m,cv:cv2,ctx:cv2.getContext("2d"),tex:t2,col,sh,
      vx:Math.cos(ang)*sp, vy:Math.sin(ang)*sp,
      life, ml:life, rot:Math.random()*Math.PI*2, rs:(Math.random()-.5)*22});
  }
  /* flash disc */
  const flash=new THREE.Mesh(new THREE.PlaneGeometry(10*scale,10*scale),
    new THREE.MeshBasicMaterial({color:type==="bandit"?hc(0xffee80):type==="client"?hc(0x88c0ff):hc(0x9090c0),transparent:true}));
  flash.position.set(x,y,3.4); sc.add(flash);

  /* popup text */
  const tc=document.createElement("canvas"); tc.width=280; tc.height=64;
  const txc=tc.getContext("2d");
  const tcolor=type==="bandit"?"#ffee44":type==="client"?"#ff5566":"#8888bb";
  txc.font="700 33px -apple-system,'Helvetica Neue',sans-serif";
  txc.textBaseline="middle"; txc.shadowColor=tcolor; txc.shadowBlur=16;
  txc.fillStyle=tcolor;
  txc.fillText(type==="bandit"?"✦ KILL! ✦":type==="client"?"✕ OPS! ✕":"MISS",8,32);
  const ttex=new THREE.CanvasTexture(tc);
  const tm=new THREE.Mesh(new THREE.PlaneGeometry(280/12,64/12),
    new THREE.MeshBasicMaterial({map:ttex,transparent:true}));
  tm.position.set(x,y+20,3.6); sc.add(tm);

  return{parts,flash,tm,e:0,dur:.85,dead:false};
}
function stepExp(exp,dt,sc){
  exp.e+=dt; const t=exp.e/exp.dur;
  if(t>=1){
    exp.parts.forEach(p=>{sc.remove(p.m);p.m.geometry.dispose();p.m.material.dispose();});
    sc.remove(exp.flash); exp.flash.geometry.dispose(); exp.flash.material.dispose();
    sc.remove(exp.tm);    exp.tm.geometry.dispose();    exp.tm.material.dispose();
    exp.dead=true; return;
  }
  exp.flash.material.opacity=Math.max(0,1-t*10); exp.flash.scale.setScalar(1+t*6);
  exp.tm.material.opacity=Math.max(0,1-t*2.2); exp.tm.position.y+=dt*28;
  exp.parts.forEach(p=>{
    p.life-=dt; if(p.life<=0){p.m.visible=false; return;}
    p.vx*=.86; p.vy*=.86; p.vy-=dt*70;
    p.m.position.x+=p.vx*dt; p.m.position.y+=p.vy*dt;
    p.rot+=p.rs*dt; p.m.rotation.z=p.rot;
    p.m.material.opacity=Math.pow(p.life/p.ml,.65);
    const{ctx,tex,col,sh}=p;
    ctx.clearRect(0,0,22,22);
    ctx.fillStyle=col; ctx.strokeStyle=col; ctx.lineWidth=2.5;
    if(sh==="rect"){ ctx.fillRect(4,4,14,14); }
    else if(sh==="star"){
      ctx.beginPath(); ctx.moveTo(11,2); ctx.lineTo(13,8); ctx.lineTo(20,8); ctx.lineTo(14,12);
      ctx.lineTo(17,20); ctx.lineTo(11,16); ctx.lineTo(5,20); ctx.lineTo(8,12);
      ctx.lineTo(2,8); ctx.lineTo(9,8); ctx.closePath(); ctx.fill();
    } else if(sh==="ring"){
      ctx.beginPath(); ctx.arc(11,11,7,0,Math.PI*2); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.moveTo(11,2); ctx.lineTo(13,9); ctx.lineTo(20,11);
      ctx.lineTo(13,13); ctx.lineTo(11,20); ctx.lineTo(9,13); ctx.lineTo(2,11);
      ctx.lineTo(9,9); ctx.closePath(); ctx.fill();
    }
    tex.needsUpdate=true;
  });
}

/* ─────────────────────────── BULLET ───────────────────────────────────────── */
function spawnBullet(sc,x0,y0,x1,y1){
  const geo=new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(x0,y0,2), new THREE.Vector3(x1,y1,2)]);
  const line=new THREE.Line(geo,
    new THREE.LineBasicMaterial({color:hc(0xffee80),transparent:true}));
  sc.add(line);
  const dot=new THREE.Mesh(new THREE.PlaneGeometry(3,3),
    new THREE.MeshBasicMaterial({color:hc(0xffffff),transparent:true}));
  dot.position.set(x1,y1,2.2); sc.add(dot);
  return{line,dot,life:.22,ml:.22};
}

/* ─────────────────────────── HUD LABEL ────────────────────────────────────── */
function mkHud(sc,text,x,y,size,col,z=3.2){
  const CW2=520,CH2=58;
  const cv=document.createElement("canvas"); cv.width=CW2; cv.height=CH2;
  const c=cv.getContext("2d");
  _ht(c,CW2,CH2,text,col,size);
  const tex=new THREE.CanvasTexture(cv);
  const m=new THREE.Mesh(new THREE.PlaneGeometry(CW2/20,CH2/20),
    new THREE.MeshBasicMaterial({map:tex,transparent:true}));
  m.position.set(x,y,z); sc.add(m);
  return{m,cv,c,tex,CW:CW2,CH:CH2,size};
}
function _ht(c,W,H,text,col,size=1){
  c.clearRect(0,0,W,H);
  const fs=Math.round(size*27);
  c.font=`700 ${fs}px -apple-system,'Helvetica Neue',Arial,sans-serif`;
  c.textBaseline="middle";
  c.shadowColor=`#${col.toString(16).padStart(6,"0")}`;
  c.shadowBlur=10;
  c.fillStyle=`#${col.toString(16).padStart(6,"0")}`;
  c.fillText(text,4,H/2);
  c.shadowBlur=0;
}
function updHud(obj,text,col){
  _ht(obj.c,obj.CW,obj.CH,text,col,obj.size);
  obj.tex.needsUpdate=true;
}

/* ─────────────────────────── SIZE ─────────────────────────────────────────── */
function calcSize(){
  const vw=window.innerWidth, vh=window.innerHeight;
  const portrait=vh>vw;
  const avH=vh-(portrait?vh*.3:vh*.24), avW=vw;
  const asp=LW/LH;
  let w,h;
  if(avW/avH>asp){ h=avH; w=h*asp; } else { w=avW; h=w/asp; }
  return{w:Math.floor(w),h:Math.floor(h)};
}

/* ─────────────────────────── INTRO SCREEN ─────────────────────────────────── */
function IntroScreen({onStart,sz}){
  const [blink,setBlink]=useState(true);
  const [glitch,setGlitch]=useState(false);
  const [tick,setTick]=useState(0);
  const [hover,setHover]=useState(false);

  useEffect(()=>{
    const bi=setInterval(()=>setBlink(b=>!b),560);
    const gi=setInterval(()=>{ setGlitch(true); setTimeout(()=>setGlitch(false),100); },2800);
    const ti=setInterval(()=>setTick(t=>t+1),60);
    return()=>{ clearInterval(bi); clearInterval(gi); clearInterval(ti); };
  },[]);

  const{w:W}=sz;
  const sc=Math.min(1,W/960);
  const r=n=>Math.round(n*sc);

  // rotating hue helpers
  const hue =(off=0)=>`hsl(${(tick*3+off)%360},100%,60%)`;
  const hueA=(off=0,a=1)=>`hsla(${(tick*3+off)%360},100%,60%,${a})`;

  const neon=(col,b=16)=>({
    color:col,
    textShadow:`0 0 ${b}px ${col},0 0 ${b*2}px ${col},0 0 ${b*3}px ${col}88`,
  });

  const rules=[
    {icon:"🚪",col:"#00ffff",label:"DOORS",  text:"3 doors open at random — CLIENT or BANDIT behind each"},
    {icon:"🔫",col:"#ffee00",label:"SHOOT",  text:"Hit BANDITS to score — multiplier climbs as speed rises"},
    {icon:"🛡",col:"#ff4466",label:"PROTECT",text:"Never shoot CLIENTS — −25 pts & −1 life per hit"},
    {icon:"💨",col:"#ff8800",label:"REACT",  text:"Bandit escapes if door closes unseen — −1 life"},
    {icon:"💀",col:"#ff44cc",label:"LIVES",  text:"3 lives total — lose them all and it's Game Over"},
  ];

  const keys=[
    {k:"←",l:"DOOR I",  c:"#ff4444"},
    {k:"↑", l:"DOOR II", c:"#ffcc00"},
    {k:"→", l:"DOOR III",c:"#44ff88"},
    {k:"▣", l:"BUTTONS", c:"#cc88ff"},
  ];

  return(
    <div style={{
      position:"fixed",inset:0,overflow:"hidden",zIndex:100,
      fontFamily:"'Courier New',Courier,monospace",
      background:"#000",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
    }}>

      {/* ─── CSS animations ─── */}
      <style>{`
        @keyframes scanroll{ from{backgroundPosition:0 0} to{backgroundPosition:0 4px} }
        @keyframes pulse80 { 0%,100%{opacity:1} 50%{opacity:.08} }
        @keyframes floatY  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes borderRotate {
          0%   { border-color: #ff0088; box-shadow: 0 0 22px #ff008866; }
          25%  { border-color: #00ffcc; box-shadow: 0 0 22px #00ffcc66; }
          50%  { border-color: #ffee00; box-shadow: 0 0 22px #ffee0066; }
          75%  { border-color: #aa44ff; box-shadow: 0 0 22px #aa44ff66; }
          100% { border-color: #ff0088; box-shadow: 0 0 22px #ff008866; }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center }
          100% { background-position:  200% center }
        }
        @keyframes ruleGlow{
          0%,100%{box-shadow:inset 0 0 0px transparent}
          50%{box-shadow:inset 0 0 18px currentColor22}
        }
      `}</style>

      {/* ── perspective floor grid ── */}
      <div style={{
        position:"absolute",bottom:0,left:0,right:0,height:"52%",
        pointerEvents:"none",zIndex:1,
        backgroundImage:`linear-gradient(${hueA(0,.2)} 1px,transparent 1px),linear-gradient(90deg,${hueA(0,.2)} 1px,transparent 1px)`,
        backgroundSize:`${r(58)}px ${r(58)}px`,
        transform:"perspective(420px) rotateX(58deg)",transformOrigin:"bottom center",
      }}/>

      {/* ── ceiling grid ── */}
      <div style={{
        position:"absolute",top:0,left:0,right:0,height:"36%",
        pointerEvents:"none",zIndex:1,
        backgroundImage:`linear-gradient(${hueA(180,.15)} 1px,transparent 1px),linear-gradient(90deg,${hueA(180,.15)} 1px,transparent 1px)`,
        backgroundSize:`${r(58)}px ${r(58)}px`,
        transform:"perspective(420px) rotateX(-58deg)",transformOrigin:"top center",
      }}/>

      {/* ── horizon sun glow ── */}
      <div style={{
        position:"absolute",bottom:"46%",left:"50%",transform:"translateX(-50%)",
        width:r(700)+"px",height:r(160)+"px",
        background:`radial-gradient(ellipse at center,${hueA(0,.45)} 0%,${hueA(60,.18)} 35%,transparent 70%)`,
        filter:"blur(10px)",pointerEvents:"none",zIndex:2,
      }}/>

      {/* ── side light streaks ── */}
      {[-1,1].map(s=>(
        <div key={s} style={{
          position:"absolute",top:0,bottom:0,
          [s<0?"left":"right"]:0,
          width:r(100)+"px",pointerEvents:"none",zIndex:2,
          background:`linear-gradient(to ${s<0?"right":"left"},${hueA(s*120,.28)},transparent)`,
        }}/>
      ))}

      {/* ── top horizontal neon line ── */}
      <div style={{
        position:"absolute",top:r(12),left:r(24),right:r(24),height:2,
        background:`linear-gradient(90deg,transparent,${hue(0)},${hue(90)},${hue(180)},${hue(270)},transparent)`,
        boxShadow:`0 0 14px ${hue(0)}`,pointerEvents:"none",zIndex:3,
      }}/>
      {/* ── bottom horizontal neon line ── */}
      <div style={{
        position:"absolute",bottom:r(12),left:r(24),right:r(24),height:2,
        background:`linear-gradient(90deg,transparent,${hue(180)},${hue(270)},${hue(0)},${hue(90)},transparent)`,
        boxShadow:`0 0 14px ${hue(180)}`,pointerEvents:"none",zIndex:3,
      }}/>

      {/* ── scanlines ── */}
      <div style={{
        position:"absolute",inset:0,pointerEvents:"none",zIndex:11,
        backgroundImage:"repeating-linear-gradient(0deg,rgba(0,0,0,.16) 0px,rgba(0,0,0,.16) 1px,transparent 1px,transparent 4px)",
        animation:"scanroll .12s linear infinite",
      }}/>

      {/* ── vignette ── */}
      <div style={{
        position:"absolute",inset:0,pointerEvents:"none",zIndex:10,
        background:"radial-gradient(ellipse at center,transparent 28%,rgba(0,0,0,.88) 100%)",
      }}/>

      {/* ─────── CONTENT ─────── */}
      <div style={{
        position:"relative",zIndex:20,
        display:"flex",flexDirection:"column",alignItems:"center",
        gap:r(11)+"px",
        padding:`${r(18)}px ${r(28)}px`,
        maxWidth:Math.min(W*.96,800)+"px",width:"100%",
      }}>

        {/* ── "ARCADE EDITION" badge ── */}
        <div style={{
          padding:`${r(3)}px ${r(20)}px`,borderRadius:r(24)+"px",
          border:`1px solid ${hue(0)}`,background:"rgba(0,0,0,.65)",
          fontSize:r(10)+"px",letterSpacing:r(5)+"px",textTransform:"uppercase",
          ...neon(hue(0),7),
          transform:glitch?"translateX(3px) skewX(-1.2deg)":"none",transition:"transform .06s",
          animation:"borderRotate 4s linear infinite",
        }}>— ARCADE EDITION —</div>

        {/* ── MAIN TITLE ── */}
        <div style={{
          textAlign:"center",lineHeight:.88,
          transform:glitch?"translateX(-4px) scaleX(1.015)":"none",transition:"transform .06s",
          animation:"floatY 3.5s ease-in-out infinite",
        }}>
          <div style={{
            fontSize:r(52)+"px",fontWeight:900,letterSpacing:r(14)+"px",
            textTransform:"uppercase",...neon("#00ffff",24),
          }}>THE</div>
          <div style={{
            fontSize:r(110)+"px",fontWeight:900,letterSpacing:r(4)+"px",
            lineHeight:.82,textTransform:"uppercase",
            backgroundImage:`linear-gradient(90deg,${hue(0)},${hue(45)},${hue(90)},${hue(135)},${hue(180)},${hue(225)},${hue(270)},${hue(315)},${hue(0)})`,
            backgroundSize:"200% auto",
            WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent",
            animation:"shimmer .8s linear infinite",
            filter:`drop-shadow(0 0 ${r(16)}px ${hue(0)}) drop-shadow(0 0 ${r(32)}px ${hue(180)}88)`,
          }}>BANK</div>
        </div>

        {/* ── rainbow separator ── */}
        <div style={{
          width:"100%",height:r(3)+"px",borderRadius:3,
          background:`linear-gradient(90deg,transparent,${hue(0)},${hue(60)},${hue(120)},${hue(180)},${hue(240)},${hue(300)},transparent)`,
          boxShadow:`0 0 18px ${hue(0)},0 0 36px ${hue(120)}66`,
        }}/>

        {/* ── HOW TO PLAY ── */}
        <div style={{
          fontSize:r(10)+"px",letterSpacing:r(4)+"px",fontWeight:700,
          textTransform:"uppercase",...neon("#ff00cc",9),
        }}>— HOW TO PLAY —</div>

        {/* ── rules list ── */}
        <div style={{
          display:"grid",gridTemplateColumns:"1fr",
          gap:r(5)+"px",width:"100%",
        }}>
          {rules.map(({icon,col,label,text},i)=>(
            <div key={i} style={{
              display:"flex",alignItems:"center",gap:r(10)+"px",
              padding:`${r(7)}px ${r(12)}px`,
              background:`linear-gradient(100deg,${col}14,${col}07 55%,transparent)`,
              border:`1px solid ${col}44`,
              borderLeft:`3px solid ${col}`,
              borderRadius:r(5)+"px",
              boxShadow:`0 0 16px ${col}12, inset 0 0 24px ${col}08`,
            }}>
              <span style={{fontSize:r(20)+"px",flexShrink:0,width:r(26)+"px",textAlign:"center"}}>{icon}</span>
              <span style={{
                fontSize:r(9)+"px",fontWeight:700,letterSpacing:"2px",
                textTransform:"uppercase",color:col,
                textShadow:`0 0 8px ${col}`,
                flexShrink:0,minWidth:r(52)+"px",
              }}>{label}</span>
              <span style={{
                fontSize:r(11)+"px",color:"rgba(210,235,255,.88)",lineHeight:1.38,letterSpacing:".3px",
              }}>{text}</span>
            </div>
          ))}
        </div>

        {/* ── controls row ── */}
        <div style={{display:"flex",gap:r(8)+"px",flexWrap:"wrap",justifyContent:"center"}}>
          {keys.map(({k,l,c})=>(
            <div key={k} style={{
              display:"flex",flexDirection:"column",alignItems:"center",gap:r(4)+"px",
              padding:`${r(9)}px ${r(16)}px`,
              border:`1px solid ${c}77`,borderRadius:r(7)+"px",
              background:`linear-gradient(145deg,${c}1c,${c}08)`,
              boxShadow:`0 0 18px ${c}44, inset 0 0 12px ${c}0c`,
              minWidth:r(60)+"px",
            }}>
              <span style={{fontSize:r(22)+"px",fontWeight:900,...neon(c,14)}}>{k}</span>
              <span style={{fontSize:r(8)+"px",color:`${c}cc`,letterSpacing:"1.5px",textTransform:"uppercase"}}>{l}</span>
            </div>
          ))}
        </div>

        {/* ── thin separator ── */}
        <div style={{
          width:"75%",height:2,borderRadius:2,
          background:`linear-gradient(90deg,transparent,${hue(200)},${hue(260)},${hue(320)},transparent)`,
          boxShadow:`0 0 10px ${hue(260)}`,
        }}/>

        {/* ── START button ── */}
        <button
          onClick={onStart}
          onMouseEnter={()=>setHover(true)}
          onMouseLeave={()=>setHover(false)}
          style={{
            background:"transparent",border:"none",outline:"none",cursor:"pointer",
            padding:`${r(8)}px ${r(32)}px`,
            opacity:blink?1:.1,transition:"opacity .14s",
          }}
        >
          <div style={{
            fontSize:r(22)+"px",fontWeight:900,
            letterSpacing:r(5)+"px",textTransform:"uppercase",
            backgroundImage:`linear-gradient(90deg,${hue(0)},${hue(60)},${hue(120)},${hue(180)},${hue(0)})`,
            backgroundSize:"200% auto",
            WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent",
            animation:"shimmer .7s linear infinite",
            filter:`drop-shadow(0 0 ${r(12)}px ${hue(0)}) drop-shadow(0 0 ${r(22)}px ${hue(120)})`,
            whiteSpace:"nowrap",
            transform:hover?"scale(1.06)":"scale(1)",transition:"transform .12s",
          }}>▶  INSERT COIN  /  START  ◀</div>
        </button>

        {/* ── footer ── */}
        <div style={{
          fontSize:r(8)+"px",color:"rgba(255,255,255,.16)",
          letterSpacing:"2px",textAlign:"center",
        }}>© 2025  THE BANK  —  v1.0  —  ALL RIGHTS RESERVED</div>

      </div>
    </div>
  );
}


/* ─────────────────────────── GAME ENGINE ──────────────────────────────────── */
function GameEngine({sz,showMsg,onStats,onLevelStateChange,onDeathStateChange}){
  const mountRef=useRef(null), rendRef=useRef(null), gRef=useRef(null);
  useEffect(()=>{ if(rendRef.current) rendRef.current.setSize(sz.w,sz.h); },[sz]);

  useEffect(()=>{
    /* renderer */
    const renderer=new THREE.WebGLRenderer({antialias:true});
    renderer.setSize(sz.w,sz.h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio,2));
    renderer.setClearColor(0x020209);
    rendRef.current=renderer;
    const mountEl = mountRef.current;
    if(mountEl) mountEl.appendChild(renderer.domElement);

    const camera=new THREE.OrthographicCamera(-LW/2,LW/2,LH/2,-LH/2,.1,100);
    camera.position.z=10;
    const scene=new THREE.Scene();
    const audio=mkAudio();

    buildScene(scene);

    /* doors */
    const dMesh=[], dOpen=[false,false,false], dTimer=[0,0,0];
    const cMesh=[null,null,null], cType=[null,null,null];
    let doorPngTexture = null;
    for(let i=0;i<3;i++){
      const cv=drawDoor(false,false,i);
      const tex=new THREE.CanvasTexture(cv);
      const baseMap = isPNGImagesActive && doorPngTexture ? doorPngTexture : tex;
      const m=new THREE.Mesh(new THREE.PlaneGeometry(DW,DH),
        new THREE.MeshBasicMaterial({map:baseMap,transparent:true}));
      m.position.set(DX[i],DOOR_CY,-2); scene.add(m);
      dMesh.push({m,cv,tex,ctx:cv.getContext("2d")});
    }

    /* door accent labels */
    const LCOLS=[0xff5555,0xf0a030,0x40dd88];
    ["DOOR  I","DOOR  II","DOOR  III"].forEach((t,i)=>{
      mkHud(scene,t,DX[i],DOOR_CY+DH/2+19,.8,LCOLS[i],-.5);
    });

    /* pre-render / preload character assets ─────────────────────────────────── */
    // PNG mode: pre-load 10 client + 10 bandit images as THREE.Texture
    // Canvas mode: pre-draw all variants once and cache as CanvasTexture

    const PNG_CLIENT_COUNT = 10;
    const PNG_BANDIT_COUNT = 10;
    const CHAR_W = DW*.84;
    const CHAR_H = DH*.9;

    // textures[type][idx] -> THREE.Texture  (populated below)
    const charTextures = { client: [], bandit: [] };

    // Resolve a PNG file name to a bundled URL, falling back to the public path.
    const resolvePngUrl = (fileName) => {
      try {
        const ctx = require.context("./img", false, /\.png$/);
        const mod = ctx(`./${fileName}`);
        return mod?.default ?? mod;
      } catch (err) {
        console.warn("[TheBankGame] PNG require failed, falling back to base path:", err);
        return `${PNG_BASE_PATH}${fileName}`;
      }
    };

    // Prepare the door PNG texture early so it can be used on first render.
    const doorPngUrlResolved = isPNGImagesActive ? resolvePngUrl("porta.png") : null;
    if(isPNGImagesActive && doorPngUrlResolved){
      const doorLoader = new THREE.TextureLoader();
      doorPngTexture = doorLoader.load(
        doorPngUrlResolved,
        (loadedTex)=>{
          loadedTex.colorSpace = THREE.SRGBColorSpace;
          dMesh.forEach((door,i)=>{ if(!dOpen[i]){ door.m.material.map = loadedTex; door.m.material.needsUpdate = true; } });
        },
        undefined,
        (err)=>console.warn("[TheBankGame] Could not load door PNG texture:", err)
      );
      doorPngTexture.colorSpace = THREE.SRGBColorSpace;
    }

    // Adjusts UVs so a texture visually covers the target aspect without empty borders.
    const applyCoverUvs = (tex, targetAspect) => {
      if(!tex?.image?.width || !tex?.image?.height) return;
      const imageAspect = tex.image.width / tex.image.height;
      if(imageAspect > targetAspect){
        const repeatX = targetAspect / imageAspect;
        tex.repeat.set(repeatX, 1);
        tex.offset.set((1 - repeatX) / 2, 0);
      } else if(imageAspect < targetAspect){
        const repeatY = imageAspect / targetAspect;
        tex.repeat.set(1, repeatY);
        tex.offset.set(0, (1 - repeatY) / 2);
      } else {
        tex.repeat.set(1, 1);
        tex.offset.set(0, 0);
      }
      tex.needsUpdate = true;
    };

    if(isPNGImagesActive){
      // Build THREE.TextureLoader textures for all PNG files
      const loader = new THREE.TextureLoader();
      // Load a texture and ensure it is in sRGB for correct gamma.
      const loadTex = (fileName) => {
        const url = resolvePngUrl(fileName);
        const tex = loader.load(
          url,
          (loadedTex) => applyCoverUvs(loadedTex, CHAR_W / CHAR_H),
          undefined,
          (err) => console.warn("[TheBankGame] Could not load texture:", url, err)
        );
        tex.colorSpace = THREE.SRGBColorSpace;   // correct gamma (Three r152+)
        return tex;
      };
      for(let i=1;i<=PNG_CLIENT_COUNT;i++)
        charTextures.client.push(loadTex(`cliente${i}.png`));
      for(let i=1;i<=PNG_BANDIT_COUNT;i++)
        charTextures.bandit.push(loadTex(`bandito${i}.png`));
    } else {
      // Programmatic canvas drawing (original behaviour)
      const cCvs = {
        client: [0,1,2,3,4,5,6,7,8].map(i=>drawChar("client",i)),
        bandit: [0,1,2].map(i=>drawChar("bandit",i)),
      };
      for(let i=0;i<9;i++)
        charTextures.client.push(new THREE.CanvasTexture(cCvs.client[i]));
      for(let i=0;i<3;i++)
        charTextures.bandit.push(new THREE.CanvasTexture(cCvs.bandit[i]));
    }

    const spawnChar=(type,idx)=>{
      const pool = charTextures[type];
      const count = pool.length || 1;
      const tex = pool[idx % count];
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(CHAR_W, CHAR_H),
        new THREE.MeshBasicMaterial({map:tex, transparent:true}));
      return m;
    };

    /* ── HUD top bar ── */
    mkRect(scene,0,LH/2-24,LW,48,0x020208,2.8);

    /* neon separator line */
    mkCanvasMesh(scene,LW,4,(c,W)=>{
      const g=c.createLinearGradient(0,0,W,0);
      g.addColorStop(0,"rgba(240,160,48,0)"); g.addColorStop(.2,"rgba(240,160,48,.7)");
      g.addColorStop(.5,"rgba(255,210,90,1)"); g.addColorStop(.8,"rgba(240,160,48,.7)");
      g.addColorStop(1,"rgba(240,160,48,0)");
      c.fillStyle=g; c.fillRect(0,0,W,4);
    }, 0,LH/2-48,LW,4,2.9,true);

    /* HUD cells */
    const hScore  =mkHud(scene,"◆ 0",      -420,LH/2-24,1.05,0xffd060);
    const hLives  =mkHud(scene,"♥ ♥ ♥",     -210,LH/2-24,1.00,0xff4466);
    const hKills  =mkHud(scene,"☠  0",        40,LH/2-24, .88,0xff7733);
    const hClients=mkHud(scene,"✓  0",        160,LH/2-24, .88,0x44ddff);
    const hMissed =mkHud(scene,"✗  0",        280,LH/2-24, .88,0x888888);
    const hAcc    =mkHud(scene,"ACC 100%",    400,LH/2-24, .88,0xaa88ff);
    const hTime   =mkHud(scene,"00:00",       430,LH/2-24, .82,0x505080);

    /* flash overlay */
    const flashM=mkRect(scene,0,0,LW,LH,0xffffff,7.5);
    flashM.material.transparent=true; flashM.material.opacity=0;

    /* door halos */
    const dHL=DX.map((x,i)=>{
      const m=new THREE.Mesh(new THREE.PlaneGeometry(DW+22,DH+22),
        new THREE.MeshBasicMaterial({color:hc(LCOLS[i]),transparent:true,opacity:0}));
      m.position.set(x,DOOR_CY,-2.7); scene.add(m);
      return{m,t:0};
    });

    const exps=[], bullets=[];
    let score=0, lives=3, kills=0, clients=0, missed=0, shots=0;
    let elapsed=0, nxT=0, nxIn=2.2, shakeT=0, shakeI=0;
    let level=1;
    let isLevelTransitioning=false;
    let levelResumeAt=0;
    let spawnResumeAt=0;
    let isDeathPaused=false;
    let deathPauseResumeAt=0;
    let gameOver=false;
    const doorResetTimeoutIds=[null,null,null];
    let banditShotDeathTimeoutId=null;

    // Pushes the latest level overlay state to the React wrapper.
    const pushLevelState=(isTransitioning,nextLevel)=>{
      if(!onLevelStateChange) return;
      onLevelStateChange({
        level:nextLevel,
        isTransitioning,
        message:isTransitioning?`Level ${nextLevel}! Ready? Start!`:"",
      });
    };

    // Pushes the latest death pause overlay state to the React wrapper.
    const pushDeathState=(isActive,reason="")=>{
      if(!onDeathStateChange) return;
      onDeathStateChange({isActive,reason});
    };

    // Calculates the level number from active gameplay seconds and caps it to 99.
    const levelFromElapsed=(activeElapsed)=>Math.min(LEVEL_MAX,Math.floor(activeElapsed/LEVEL_DURATION_SECONDS)+1);

    // Builds an effective difficulty time that keeps the same curve shape but advances at half speed.
    // This slows every difficulty-driven system (spawn rate, door open time, max doors, bandit chance)
    // to roughly 50% of the previous ramp while leaving level timing and transition mechanics unchanged.
    const difficultySeconds=(activeElapsed,currentLevel)=>(activeElapsed+Math.max(0,currentLevel-1)*2.5)*0.5;

    // Clears any pending delayed door redraw for one door to avoid stale timer callbacks.
    const clearDoorResetTimeout=(doorIndex)=>{
      if(doorResetTimeoutIds[doorIndex]!==null){
        clearTimeout(doorResetTimeoutIds[doorIndex]);
        doorResetTimeoutIds[doorIndex]=null;
      }
    };

    // Schedules a safe delayed redraw for a door and cancels any previous delayed redraw on that same door.
    const scheduleDoorClosedRedraw=(doorIndex,delayMs)=>{
      clearDoorResetTimeout(doorIndex);
      doorResetTimeoutIds[doorIndex]=setTimeout(()=>{
        redoor(doorIndex,false,false);
        doorResetTimeoutIds[doorIndex]=null;
      },delayMs);
    };

    // Force-closes every door, removes visible characters, and resets all per-door timers/state without penalties.
    const forceCloseAllDoorsForTransition=()=>{
      for(let i=0;i<3;i++){
        clearDoorResetTimeout(i);
        if(cMesh[i]){ scene.remove(cMesh[i]); cMesh[i]=null; }
        cType[i]=null;
        dOpen[i]=false;
        dTimer[i]=0;
        redoor(i,false,false);
      }
    };

    // Starts the inter-level pause overlay and freezes gameplay until the short transition timer expires.
    const startLevelTransition=(nextLevel,nowMs)=>{
      level=nextLevel;
      forceCloseAllDoorsForTransition();
      nxT=0;
      nxIn=spawnInterval(difficultySeconds(elapsed,level));
      isLevelTransitioning=true;
      levelResumeAt=nowMs+LEVEL_TRANSITION_MS;
      spawnResumeAt=levelResumeAt+LEVEL_POST_TRANSITION_SPAWN_DELAY_MS;
      pushLevelState(true,level);
    };

    // Starts a fixed 3-second gameplay freeze and reports the death reason to the React overlay layer.
    const startDeathPause=(reason,nowMs)=>{
      isDeathPaused=true;
      deathPauseResumeAt=nowMs+3000;
      pushDeathState(true,reason);
    };

    // Clears the delayed timeout that starts the bandit-shot death overlay flow.
    const clearBanditShotDeathTimeout=()=>{
      if(banditShotDeathTimeoutId!==null){
        clearTimeout(banditShotDeathTimeoutId);
        banditShotDeathTimeoutId=null;
      }
    };

    pushLevelState(false,1);
    pushDeathState(false,"");

    // Difficulty curve — everything is a function of elapsed seconds
    // spawn interval: starts ~2.2s, reaches ~0.5s at 120s, ~0.15s at 300s
    const spawnInterval=t=> Math.max(0.13, 2.2 * Math.pow(0.982, t));
    // door open time: starts 3-6s, reaches 0.6-1.0s at 180s
    const doorOpenTime=t=> {
      const base=Math.max(0.55, 3.0 * Math.pow(0.984, t));
      const variance=Math.max(0.12, 0.5 * Math.pow(0.988, t));
      return base + Math.random()*variance;
    };
    // max simultaneous open doors: 1→2→3 then at high speed allow rapid cycling
    const maxDoors=t=> t<15?1 : t<40?2 : 3;
    // bandit probability: 32% → 65% over 3 minutes
    const banditProb=t=> Math.min(0.65, 0.32 + t/380);

    const redoor=(i,open,boom=false)=>{
      const d=dMesh[i];
      if(isPNGImagesActive && doorPngTexture){
        d.m.material.map = doorPngTexture;
        d.m.material.needsUpdate = true;
        return;
      }
      d.ctx.clearRect(0,0,DW,DH);
      d.ctx.drawImage(drawDoor(open,boom,i),0,0);
      d.tex.needsUpdate=true;
      if(d.m.material.map !== d.tex){
        d.m.material.map = d.tex;
        d.m.material.needsUpdate = true;
      }
    };

    if(isPNGImagesActive){
      // Ensure the closed door PNG is loaded and re-rendered once available.
      ensureDoorPngLoaded(resolvePngUrl, () => {
        dMesh.forEach((_,i)=>{ if(!dOpen[i]) redoor(i,false,false); });
      });
    }
    const openDoor=(i,type,ci)=>{
      audio.doorOpen();
      redoor(i,true);
      const m=spawnChar(type,ci);
      m.position.set(DX[i],DOOR_CY-DH*.02,.9);
      scene.add(m); if(cMesh[i]) scene.remove(cMesh[i]);
      cMesh[i]=m; cType[i]=type; dOpen[i]=true;
      dTimer[i]=doorOpenTime(difficultySeconds(elapsed,level));
    };
    const closeDoor=(i,penalise=true)=>{
      if(!dOpen[i]) return;
      if(penalise && cType[i]==="bandit"){
        lives=Math.max(0,lives-1);
        showMsg("THE BANDIT SHOT YOU −♥","red");
        audio.escape();
        audio.shot();
        flashM.material.color=hc(0xff2020); flashM.material.opacity=.42;
        setTimeout(()=>{ flashM.material.opacity=0; flashM.material.color=hc(0xffffff); },260);
        if(lives<=0){
          gameOver=true;
          clearBanditShotDeathTimeout();
          pushDeathState(false,"");
          onStats({score,lives,kills,clients,missed,shots});
        } else {
          isDeathPaused=true;
          deathPauseResumeAt=Number.POSITIVE_INFINITY;
          pushDeathState(false,"");
          clearBanditShotDeathTimeout();
          banditShotDeathTimeoutId=setTimeout(()=>{
            startDeathPause("a bandit shot you",performance.now());
            banditShotDeathTimeoutId=null;
          },BANDIT_SHOT_PRE_DEATH_MS);
        }
      }
      redoor(i,false);
      if(cMesh[i]){ scene.remove(cMesh[i]); cMesh[i]=null; }
      cType[i]=null; dOpen[i]=false; dTimer[i]=0;
      onStats({score,lives,kills,clients,missed,shots});
    };

    const shoot=(di)=>{
      if(gameOver||isLevelTransitioning||isDeathPaused) return;
      shots++;
      audio.shot(); shakeI=5; shakeT=.17;
      bullets.push(spawnBullet(scene,0,DOOR_CY-DH*.55,DX[di],DOOR_CY));
      dHL[di].m.material.opacity=1; dHL[di].m.material.color=hc(0xffffff); dHL[di].t=.52;

      if(!dOpen[di]){
        missed++;
        audio.miss(); showMsg("MISSED!","gray");
        exps.push(spawnExp(scene,DX[di],DOOR_CY,"miss",1.2));
        redoor(di,false,true); scheduleDoorClosedRedraw(di,480);
        onStats({score,lives,kills,clients,missed,shots}); return;
      }

      const type=cType[di];
      exps.push(spawnExp(scene,DX[di],DOOR_CY,type,1.65));
      redoor(di,false,true); scheduleDoorClosedRedraw(di,530);

      if(type==="bandit"){
        const multi=1+Math.floor(elapsed/45);
        const pts=(10+Math.floor(Math.random()*15))*multi;
        score+=pts; kills++;
        showMsg(`✦  +${pts}${multi>1?" ×"+multi:""}  KILL  ✦`,"green");
        audio.kill();
      } else {
        score=Math.max(0,score-25); lives=Math.max(0,lives-1); clients++;
        showMsg("CIVILIAN HIT  −25  −♥","red");
        audio.oops();
        if(lives<=0){
          gameOver=true;
          pushDeathState(false,"");
        } else {
          startDeathPause("you killed a civilian",performance.now());
        }
      }
      if(cMesh[di]){ scene.remove(cMesh[di]); cMesh[di]=null; }
      cType[di]=null; dOpen[di]=false; dTimer[di]=0;
      onStats({score,lives,kills,clients,missed,shots});
    };

    gRef.current={shoot};
    const onKey=e=>{
      if(e.key==="ArrowLeft"){ e.preventDefault(); shoot(0); }
      if(e.key==="ArrowUp")  { e.preventDefault(); shoot(1); }
      if(e.key==="ArrowRight"){ e.preventDefault(); shoot(2); }
    };
    window.addEventListener("keydown",onKey);
    renderer.domElement.style.pointerEvents="none";

    const onFire=e=>{ if(gRef.current) gRef.current.shoot(e.detail); };
    document.addEventListener("gamefire",onFire);

    let lastT=performance.now(),raf;
    function animate(){
      raf=requestAnimationFrame(animate);
      const now=performance.now(), dt=Math.min((now-lastT)/1000,.1); lastT=now;

      // Holds the game completely still during level transition overlays and resumes automatically after a short delay.
      if(isLevelTransitioning){
        if(now>=levelResumeAt){
          isLevelTransitioning=false;
          pushLevelState(false,level);
        } else {
          renderer.render(scene,camera);
          return;
        }
      }

      // Freezes all gameplay systems for exactly 3 seconds after a death event, then resumes automatically.
      if(isDeathPaused){
        if(now>=deathPauseResumeAt){
          isDeathPaused=false;
          pushDeathState(false,"");
        } else {
          renderer.render(scene,camera);
          return;
        }
      }

      // Keeps gameplay logic frozen for exactly one second after the overlay ends so doors restart from a clean closed state.
      if(now<spawnResumeAt){
        renderer.render(scene,camera);
        return;
      }

      if(!gameOver){
        elapsed+=dt;
        const nextLevel=levelFromElapsed(elapsed);
        if(nextLevel>level){
          startLevelTransition(nextLevel,now);
          renderer.render(scene,camera);
          return;
        }
      }

      const diffTime=difficultySeconds(elapsed,level);

      /* HUD update */
      const secs=Math.floor(elapsed);
      const acc=shots>0?Math.round((kills)/(shots)*100):100;
      const lStr=lives>0?"♥ ".repeat(lives).trim()+" "+"♡ ".repeat(Math.max(0,3-lives)).trim():"☠  GAME OVER";
      updHud(hScore,  `◆  ${score}`,               0xffd060);
      updHud(hLives,  lStr,                         lives>1?0xff4466:lives===1?0xff8822:0xff2020);
      updHud(hKills,  `☠  ${kills}`,                0xff7733);
      updHud(hClients,`✓  ${clients}`,              0x44ddff);
      updHud(hMissed, `✗  ${missed}`,               0x777799);
      updHud(hAcc,    `ACC ${acc}%`,                acc>=70?0xaa88ff:acc>=40?0xffaa44:0xff4444);
      updHud(hTime,   `${String(Math.floor(secs/60)).padStart(2,"0")}:${String(secs%60).padStart(2,"0")}`, 0x505080);

      if(!gameOver){
        /* door timers */
        for(let i=0;i<3;i++) if(dOpen[i]){ dTimer[i]-=dt; if(dTimer[i]<=0) closeDoor(i,true); }
        /* spawn */
        nxT+=dt;
        if(nxT>=nxIn){
          nxT=0;
          nxIn=spawnInterval(diffTime);
          const max=maxDoors(diffTime), oc=dOpen.filter(Boolean).length;
          if(oc<max){
            const free=[0,1,2].filter(i=>!dOpen[i]);
            if(free.length){
              const di=free[Math.floor(Math.random()*free.length)];
               const isBandit=Math.random()<banditProb(diffTime);
               openDoor(di,isBandit?"bandit":"client",Math.floor(Math.random()*(isBandit?3:9)));
            }
          }
          // At high speed, also try to fill all free doors immediately
          if(diffTime>90){
            const free2=[0,1,2].filter(i=>!dOpen[i]);
            free2.forEach(di=>{
              if(Math.random()<Math.min(0.85,(diffTime-90)/120)){
                const isBandit=Math.random()<banditProb(diffTime);
                openDoor(di,isBandit?"bandit":"client",Math.floor(Math.random()*(isBandit?3:9)));
              }
            });
          }
        }
      }

      /* halos */
      dHL.forEach(h=>{ if(h.t>0){ h.t-=dt; h.m.material.opacity=Math.max(0,h.t/.52); } });

      /* bullets */
      for(let i=bullets.length-1;i>=0;i--){
        const b=bullets[i]; b.life-=dt;
        const a=Math.max(0,b.life/b.ml);
        b.line.material.opacity=a; b.dot.material.opacity=a;
        if(b.life<=0){
          scene.remove(b.line); b.line.geometry.dispose(); b.line.material.dispose();
          scene.remove(b.dot);  b.dot.geometry.dispose();  b.dot.material.dispose();
          bullets.splice(i,1);
        }
      }
      /* explosions */
      for(let i=exps.length-1;i>=0;i--){
        stepExp(exps[i],dt,scene); if(exps[i].dead) exps.splice(i,1);
      }
      /* camera shake */
      if(shakeT>0){
        shakeT-=dt;
        const s=shakeI*(shakeT/.17);
        camera.position.x=(Math.random()-.5)*s; camera.position.y=(Math.random()-.5)*s;
      } else { camera.position.x=0; camera.position.y=0; }

      renderer.render(scene,camera);
    }
    animate();

    return()=>{
      cancelAnimationFrame(raf);
      pushLevelState(false,1);
      pushDeathState(false,"");
      clearBanditShotDeathTimeout();
      for(let i=0;i<3;i++) clearDoorResetTimeout(i);
      window.removeEventListener("keydown",onKey);
      document.removeEventListener("gamefire",onFire);
      renderer.dispose();
      if(mountEl?.contains(renderer.domElement)) mountEl.removeChild(renderer.domElement);
      rendRef.current=null;
    };
  },[]);// eslint-disable-line

  return <div ref={mountRef} style={{position:"absolute",inset:0,overflow:"hidden"}}/>;
}

/* ─────────────────────────── WRAPPER ──────────────────────────────────────── */
export default function TheBankGame(){
  const [screen,setScreen]=useState("intro");
  const [gameKey,setGameKey]=useState(0);
  const [msg,setMsg]=useState(null);
  const [sz,setSz]=useState(calcSize);
  const [stats,setStats]=useState({score:0,lives:3,kills:0,clients:0,missed:0,shots:0});
  const [levelState,setLevelState]=useState({level:1,isTransitioning:false,message:""});
  const [deathState,setDeathState]=useState({isActive:false,reason:""});
  const audioRef=useRef(null);

  const showMsg=useCallback((text,color="yellow")=>{
    setMsg({text,color,id:Date.now()});
    setTimeout(()=>setMsg(null),1900);
  },[]);

  useEffect(()=>{
    const onR=()=>setSz(calcSize());
    window.addEventListener("resize",onR);
    window.addEventListener("orientationchange",()=>setTimeout(onR,200));
    return()=>window.removeEventListener("resize",onR);
  },[]);

  useEffect(()=>{
    // Create the background soundtrack element once and keep it for the session.
    const audio=new Audio(soundtrackUrl);
    audio.loop=true;
    audio.volume=0.35;
    audioRef.current=audio;
    return()=>{
      // Stop and release the soundtrack when the component unmounts.
      audio.pause();
      audio.currentTime=0;
      audioRef.current=null;
    };
  },[]);

  useEffect(()=>{
    // Play or pause the soundtrack depending on the current screen.
    const audio=audioRef.current;
    if(!audio) return;
    if(screen==="playing"){
      audio.play().catch(()=>{});
      return;
    }
    audio.pause();
    audio.currentTime=0;
  },[screen]);

  // Reset state and start a new session with background music.
  const startGame=()=>{
    setStats({score:0,lives:3,kills:0,clients:0,missed:0,shots:0});
    setLevelState({level:1,isTransitioning:false,message:""});
    setDeathState({isActive:false,reason:""});
    setMsg(null);
    setScreen("playing");
    setGameKey(k=>k+1);
    const audio=audioRef.current;
    if(audio){
      audio.currentTime=0;
      audio.play().catch(()=>{});
    }
  };
  // Reset state and continue the current session with background music.
  const restart=()=>{
    setStats({score:0,lives:3,kills:0,clients:0,missed:0,shots:0});
    setLevelState({level:1,isTransitioning:false,message:""});
    setDeathState({isActive:false,reason:""});
    setMsg(null);
    setGameKey(k=>k+1);
    const audio=audioRef.current;
    if(audio){
      audio.currentTime=0;
      audio.play().catch(()=>{});
    }
  };
  const fire   =d => document.dispatchEvent(new CustomEvent("gamefire",{detail:d}));

  const{w:CW,h:CH}=sz;
  const acc =stats.shots>0?Math.round(stats.kills/stats.shots*100):100;
  const gameOver=stats.lives<=0;

  return(
    <div style={{
      background:"#020209",width:"100vw",height:"100dvh",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      fontFamily:"-apple-system,'Helvetica Neue',Arial,sans-serif",
      userSelect:"none",overflow:"hidden",
    }}>

      {screen==="intro" && <IntroScreen onStart={startGame} sz={sz}/>}
      {screen==="playing" && <>

      {/* ════════════ TOP HUD BAR ════════════ */}
      <div style={{
        display:"flex",alignItems:"center",justifyContent:"space-between",
        width:CW+"px",
        marginBottom:Math.max(5,CH/110)+"px",
        gap:Math.max(5,CW/140)+"px",
        fontFamily:"'Courier New',Courier,monospace",
      }}>

        {/* SCORE — big pill on left */}
        <div style={{
          display:"flex",alignItems:"center",gap:Math.max(6,CW/130)+"px",
          padding:`${Math.max(4,CH/130)}px ${Math.max(10,CW/75)}px`,
          background:"linear-gradient(135deg,rgba(255,210,60,.18),rgba(255,160,20,.08))",
          border:"1px solid rgba(255,210,60,.6)",
          borderRadius:Math.max(5,CW/170)+"px",
          boxShadow:"0 0 18px rgba(255,200,40,.35), inset 0 0 14px rgba(255,200,40,.08)",
          flexShrink:0,
        }}>
          <span style={{fontSize:Math.max(9,CW/86)+"px",color:"rgba(255,210,60,.55)",letterSpacing:"2px"}}>SCORE</span>
          <span style={{
            fontSize:Math.max(14,CW/48)+"px",fontWeight:900,color:"#ffd060",
            textShadow:"0 0 14px #ffd060, 0 0 28px #ffd06088",letterSpacing:"1px",
          }}>{stats.score}</span>
        </div>

        {/* centre cluster: level / kills / clients / missed / acc */}
        <div style={{display:"flex",gap:Math.max(4,CW/160)+"px",flex:1,justifyContent:"center",flexWrap:"nowrap"}}>
          {[
            {icon:"⬢",v:levelState.level,label:"LEVEL",c:"#ffee66",bg:"rgba(255,230,90,.12)",bd:"rgba(255,220,80,.5)"},
            {icon:"☠",v:stats.kills,   label:"KILLS",   c:"#ff6633",bg:"rgba(255,80,30,.12)", bd:"rgba(255,90,30,.5)"},
            {icon:"✓",v:stats.clients, label:"SAFE",    c:"#44ddff",bg:"rgba(40,200,255,.1)",  bd:"rgba(40,200,255,.45)"},
            {icon:"✗",v:stats.missed,  label:"MISSED",  c:"#8888bb",bg:"rgba(100,100,180,.1)",  bd:"rgba(120,120,200,.35)"},
            {icon:"⊕",v:(stats.shots>0?Math.round(stats.kills/stats.shots*100):100)+"%",
             label:"ACC",c:acc>=70?"#bb88ff":acc>=40?"#ffaa44":"#ff4444",
             bg:"rgba(100,60,200,.1)",bd:"rgba(140,100,240,.4)"},
          ].map(({icon,v,label,c,bg,bd})=>(
            <div key={label} style={{
              display:"flex",flexDirection:"column",alignItems:"center",
              padding:`${Math.max(3,CH/170)}px ${Math.max(7,CW/110)}px`,
              background:bg,border:`1px solid ${bd}`,
              borderRadius:Math.max(4,CW/200)+"px",
              boxShadow:`0 0 12px ${bd}`,
              minWidth:Math.max(36,CW/22)+"px",flexShrink:0,
            }}>
              <span style={{
                fontSize:Math.max(10,CW/60)+"px",fontWeight:900,
                color:c,textShadow:`0 0 10px ${c}`,letterSpacing:".5px",
              }}>{icon} {v}</span>
              <span style={{fontSize:Math.max(7,CW/108)+"px",color:`${c}88`,letterSpacing:"1.5px",textTransform:"uppercase"}}>{label}</span>
            </div>
          ))}
        </div>

        {/* LIVES — right pill */}
        <div style={{
          display:"flex",alignItems:"center",gap:Math.max(4,CW/180)+"px",
          padding:`${Math.max(4,CH/130)}px ${Math.max(10,CW/80)}px`,
          background:stats.lives>1?"linear-gradient(135deg,rgba(255,60,100,.15),rgba(255,40,80,.07))":"linear-gradient(135deg,rgba(255,30,30,.25),rgba(180,0,0,.12))",
          border:`1px solid ${stats.lives>1?"rgba(255,80,110,.55)":"rgba(255,40,40,.7)"}`,
          borderRadius:Math.max(5,CW/170)+"px",
          boxShadow:`0 0 18px ${stats.lives>1?"rgba(255,60,100,.3)":"rgba(255,0,0,.45)"},inset 0 0 12px rgba(255,60,80,.08)`,
          flexShrink:0,
          animation:stats.lives===1?"pulse1 .6s ease-in-out infinite":"none",
        }}>
          <span style={{fontSize:Math.max(9,CW/86)+"px",color:"rgba(255,100,130,.55)",letterSpacing:"2px"}}>LIVES</span>
          {[0,1,2].map(i=>(
            <span key={i} style={{
              fontSize:Math.max(14,CW/52)+"px",
              color:i<stats.lives?"#ff4466":"rgba(255,255,255,.12)",
              textShadow:i<stats.lives?"0 0 12px #ff4466, 0 0 24px #ff446688":"none",
              transition:"all .2s",
            }}>♥</span>
          ))}
        </div>
      </div>

      {/* ════════════ VIEWPORT ════════════ */}
      <div style={{
        position:"relative",width:CW+"px",height:CH+"px",flexShrink:0,
        borderRadius:3,overflow:"hidden",
        boxShadow:`
          0 0 0 1px rgba(240,160,48,.45),
          0 0 28px rgba(240,100,30,.22),
          0 0 64px rgba(140,70,255,.14),
          0 0 120px rgba(0,180,255,.07),
          inset 0 0 70px rgba(0,0,20,.55)
        `,
      }}>
        <GameEngine
          key={gameKey}
          sz={sz}
          showMsg={showMsg}
          onStats={setStats}
          onLevelStateChange={setLevelState}
          onDeathStateChange={setDeathState}
        />

        {/* Full-screen level transition overlay that freezes action between levels. */}
        {levelState.isTransitioning&&!gameOver&&(
          <div style={{
            position:"fixed",inset:0,zIndex:9500,
            background:"radial-gradient(ellipse at center,rgba(16,10,34,.94) 0%,rgba(6,2,14,.985) 100%)",
            display:"flex",alignItems:"center",justifyContent:"center",
            pointerEvents:"none",
            fontFamily:"'Courier New',Courier,monospace",
          }}>
            <div style={{
              fontSize:Math.max(26,CW/11)+"px",
              fontWeight:900,
              letterSpacing:"2px",
              color:"#ffee66",
              textAlign:"center",
              textShadow:"0 0 20px #ffee66, 0 0 42px rgba(255,238,102,.55)",
              animation:"shimmer 1.2s linear infinite",
              background:"linear-gradient(90deg,#ffda66,#ffee99,#ffda66)",
              backgroundSize:"200% auto",
              WebkitBackgroundClip:"text",
              backgroundClip:"text",
              WebkitTextFillColor:"transparent",
            }}>{levelState.message}</div>
          </div>
        )}

        {/* Full-screen death pause overlay shown for exactly 3 seconds on specific death reasons. */}
        {deathState.isActive&&!gameOver&&(
          <div style={{
            position:"fixed",inset:0,zIndex:9600,
            background:"radial-gradient(ellipse at center,rgba(30,6,6,.96) 0%,rgba(8,2,8,.99) 100%)",
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            pointerEvents:"none",
            textAlign:"center",
            fontFamily:"'Courier New',Courier,monospace",
            padding:"20px",
            gap:Math.max(10,CH/40)+"px",
          }}>
            <div style={{
              fontSize:Math.max(28,CW/10)+"px",
              fontWeight:900,
              letterSpacing:"2px",
              color:"#ff8e8e",
              textShadow:"0 0 24px #ff4a4a, 0 0 48px rgba(255,74,74,.5)",
            }}>Oh no! You&apos;re dead!</div>
            <div style={{
              fontSize:Math.max(14,CW/35)+"px",
              fontWeight:700,
              letterSpacing:"1px",
              color:"rgba(255,210,210,.95)",
              textShadow:"0 0 16px rgba(255,190,190,.45)",
              textTransform:"lowercase",
            }}>{deathState.reason}</div>
          </div>
        )}

        {/* feedback toast */}
        {msg&&(
          <div key={msg.id} style={{
            position:"absolute",top:"30%",left:"50%",
            transform:"translate(-50%,-50%)",pointerEvents:"none",
            fontSize:Math.max(15,CW/20)+"px",fontWeight:900,
            letterSpacing:"1px",whiteSpace:"nowrap",
            fontFamily:"'Courier New',Courier,monospace",
            color:msg.color==="red"?"#ff5050":msg.color==="green"?"#44ff99":msg.color==="gray"?"#7878aa":"#ffee44",
            textShadow:"0 0 22px currentColor, 0 0 44px currentColor",
            animation:"msgPop 1.9s ease forwards",zIndex:20,
          }}>{msg.text}</div>
        )}

        {/* ── GAME OVER overlay ── */}
        {gameOver&&(
          <div style={{
            position:"fixed",inset:0,zIndex:9999,
            background:"radial-gradient(ellipse at center,rgba(20,4,4,.97) 0%,rgba(4,2,14,.99) 100%)",
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            gap:Math.max(12,CH/34)+"px",
            fontFamily:"'Courier New',Courier,monospace",
          }}>
            {/* scanlines */}
            <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:1,
              backgroundImage:"repeating-linear-gradient(0deg,rgba(0,0,0,.14) 0px,rgba(0,0,0,.14) 1px,transparent 1px,transparent 4px)"}}/>
            {/* corner decorations */}
            {[[0,0,"right","bottom"],[1,0,"left","bottom"],[0,1,"right","top"],[1,1,"left","top"]].map(([cx,cy,bx,by])=>(
              <div key={cx+","+cy} style={{
                position:"absolute",[cx===0?"left":"right"]:16,[cy===0?"top":"bottom"]:16,
                width:32,height:32,pointerEvents:"none",zIndex:2,
                borderTop:cy===0?"2px solid rgba(255,100,100,.5)":"none",
                borderBottom:cy===1?"2px solid rgba(255,100,100,.5)":"none",
                borderLeft:cx===0?"2px solid rgba(255,100,100,.5)":"none",
                borderRight:cx===1?"2px solid rgba(255,100,100,.5)":"none",
              }}/>
            ))}

            {/* GAME OVER text */}
            <div style={{
              position:"relative",zIndex:3,textAlign:"center",
              fontSize:Math.max(28,CW/9)+"px",fontWeight:900,letterSpacing:"6px",
              textTransform:"uppercase",
              background:"linear-gradient(90deg,#ff1a1a,#ff6622,#ff1a1a)",
              backgroundSize:"200% auto",
              WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent",
              filter:"drop-shadow(0 0 22px #ff2020) drop-shadow(0 0 44px #ff202066)",
              animation:"shimmer .9s linear infinite",
            }}>GAME  OVER</div>

            {/* stat cards */}
            <div style={{
              position:"relative",zIndex:3,
              display:"grid",
              gridTemplateColumns:`repeat(${CW>500?5:3},1fr)`,
              gap:Math.max(8,CW/85)+"px",
              padding:`${Math.max(12,CH/42)}px ${Math.max(16,CW/52)}px`,
              background:"rgba(255,255,255,.04)",
              border:"1px solid rgba(255,255,255,.1)",
              borderRadius:Math.max(8,CW/100)+"px",
              backdropFilter:"blur(12px)",
            }}>
              {[
                {l:"SCORE",   v:String(stats.score),  c:"#ffd060",ic:"◆"},
                {l:"KILLS",   v:String(stats.kills),   c:"#ff6633",ic:"☠"},
                {l:"SAFE",    v:String(stats.clients),  c:"#44ddff",ic:"✓"},
                {l:"MISSED",  v:String(stats.missed),  c:"#8888bb",ic:"✗"},
                {l:"ACCURACY",v:acc+"%",               c:acc>=70?"#bb88ff":acc>=40?"#ffaa44":"#ff4444",ic:"⊕"},
              ].map(({l,v,c,ic})=>(
                <div key={l} style={{textAlign:"center",padding:`${Math.max(8,CH/56)}px ${Math.max(8,CW/90)}px`}}>
                  <div style={{
                    fontSize:Math.max(18,CW/30)+"px",fontWeight:900,
                    color:c,textShadow:`0 0 18px ${c},0 0 36px ${c}66`,
                    marginBottom:4,letterSpacing:"1px",
                  }}>{ic} {v}</div>
                  <div style={{
                    color:"rgba(255,255,255,.3)",fontSize:Math.max(7,CW/100)+"px",
                    letterSpacing:"2.5px",textTransform:"uppercase",
                  }}>{l}</div>
                </div>
              ))}
            </div>

            {/* buttons row */}
            <div style={{position:"relative",zIndex:3,display:"flex",gap:Math.max(10,CW/80)+"px"}}>
              <button onClick={restart}
                onMouseEnter={e=>e.currentTarget.style.filter="brightness(1.25) saturate(1.2)"}
                onMouseLeave={e=>e.currentTarget.style.filter=""}
                style={{
                  padding:`${Math.max(11,CH/48)}px ${Math.max(28,CW/24)}px`,
                  background:"linear-gradient(135deg,#f0a030,#d07010,#f0a030)",
                  backgroundSize:"200% auto",
                  color:"#060202",border:"none",borderRadius:Math.max(6,CW/150)+"px",
                  fontSize:Math.max(12,CW/52)+"px",fontWeight:900,letterSpacing:"3px",
                  cursor:"pointer",textTransform:"uppercase",
                  boxShadow:"0 0 30px rgba(240,160,48,.65),0 4px 20px rgba(0,0,0,.5)",
                  transition:"filter .12s",animation:"shimmer 1.2s linear infinite",
                }}>▶  PLAY AGAIN</button>

              <button onClick={()=>setScreen("intro")}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,.55)";e.currentTarget.style.color="#fff";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,.2)";e.currentTarget.style.color="rgba(255,255,255,.5)";}}
                style={{
                  padding:`${Math.max(11,CH/48)}px ${Math.max(18,CW/38)}px`,
                  background:"rgba(255,255,255,.04)",
                  color:"rgba(255,255,255,.5)",
                  border:"1px solid rgba(255,255,255,.2)",
                  borderRadius:Math.max(6,CW/150)+"px",
                  fontSize:Math.max(10,CW/65)+"px",fontWeight:700,letterSpacing:"2px",
                  cursor:"pointer",textTransform:"uppercase",transition:"border-color .12s, color .12s",
                }}>← MENU</button>
            </div>
          </div>
        )}
      </div>

      {/* ════════════ SHOOT BUTTONS ════════════ */}
      <div style={{
        display:"flex",gap:Math.max(6,CW/120)+"px",
        marginTop:Math.max(6,CH/95)+"px",
        width:CW+"px",
        fontFamily:"'Courier New',Courier,monospace",
      }}>
        {[
          {lbl:"DOOR  I",  k:"←",d:0,
           bg:"linear-gradient(145deg,rgba(255,50,50,.22),rgba(200,30,20,.08))",
           bd:"rgba(255,80,60,.65)",gw:"rgba(255,50,40,.55)",c:"#ff6655",
           activeBg:"linear-gradient(145deg,rgba(255,100,80,.55),rgba(255,60,40,.3))"},
          {lbl:"DOOR  II", k:"↑",d:1,
           bg:"linear-gradient(145deg,rgba(255,180,40,.2),rgba(220,140,20,.08))",
           bd:"rgba(255,190,50,.65)",gw:"rgba(240,160,40,.55)",c:"#ffcc44",
           activeBg:"linear-gradient(145deg,rgba(255,210,80,.55),rgba(240,170,30,.3))"},
          {lbl:"DOOR  III",k:"→",d:2,
           bg:"linear-gradient(145deg,rgba(40,230,110,.17),rgba(20,180,80,.07))",
           bd:"rgba(60,230,110,.65)",gw:"rgba(40,210,100,.5)",c:"#44ff88",
           activeBg:"linear-gradient(145deg,rgba(80,255,140,.5),rgba(40,220,100,.28))"},
        ].map(({lbl,k,d,bg,bd,gw,c,activeBg})=>(
          <button key={d}
            onPointerDown={e=>{fire(d);e.currentTarget.style.background=activeBg;e.currentTarget.style.transform="scale(.94) translateY(2px)";e.currentTarget.style.boxShadow=`0 0 40px ${gw},0 2px 4px rgba(0,0,0,.3)`;}}
            onPointerUp  ={e=>{e.currentTarget.style.background=bg;e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=`0 0 20px ${gw},0 4px 14px rgba(0,0,0,.45)`;}}
            onPointerLeave={e=>{e.currentTarget.style.background=bg;e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=`0 0 20px ${gw},0 4px 14px rgba(0,0,0,.45)`;}}
            style={{
              flex:1,
              padding:`${Math.max(10,CH/52)}px 0`,
              background:bg,color:c,
              border:`1px solid ${bd}`,
              borderRadius:Math.max(6,CW/155)+"px",
              fontSize:Math.max(12,CW/55)+"px",
              fontWeight:900,letterSpacing:"1.5px",
              cursor:"pointer",lineHeight:1.25,
              textTransform:"uppercase",
              textShadow:`0 0 14px ${c},0 0 28px ${c}88`,
              boxShadow:`0 0 20px ${gw},0 4px 14px rgba(0,0,0,.45)`,
              transition:"transform .07s, box-shadow .07s, background .07s",
              userSelect:"none",WebkitUserSelect:"none",
            }}>
            {lbl}
            <br/>
            <span style={{fontSize:Math.max(8,CW/105)+"px",opacity:.4,fontWeight:500,letterSpacing:"2px"}}>[{k}]</span>
          </button>
        ))}
      </div>

      <style>{`
        *{box-sizing:border-box;}
        @keyframes msgPop{
          0%  {transform:translate(-50%,-50%) scale(1.6);opacity:0;}
          10% {transform:translate(-50%,-50%) scale(1.02);opacity:1;}
          60% {transform:translate(-50%,-66%) scale(1);opacity:1;}
          100%{transform:translate(-50%,-92%) scale(.88);opacity:0;}
        }
        @keyframes pulse1{
          0%,100%{box-shadow:0 0 18px rgba(255,0,0,.45),inset 0 0 12px rgba(255,60,80,.08);}
          50%{box-shadow:0 0 36px rgba(255,0,0,.85),inset 0 0 20px rgba(255,60,80,.18);}
        }
        @keyframes shimmer{
          0%{background-position:-200% center}
          100%{background-position:200% center}
        }
      `}</style>
      </>}

    </div>
  );
}
